'use client';

import dynamic from 'next/dynamic';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styles from './ProductDetail.module.css';
import { OptimizedImage } from '@/components/OptimizedImage/OptimizedImage';
import { getProductByPathname, getProductsByCategory, getRelatedProducts, type Product } from '@/data/products';
import {
  type DensityDns,
  type ProductLine,
  DENSITY_OPTIONS,
  PRODUCT_LINE_THICKNESSES,
  buildTopSpecs,
  getDefaultThickness,
  getDefaultAkustikThickness,
  getAkustikPrice,
  getAkustikThicknessOptions,
  getHighlightSpecs,
  getProductLine,
  getSalesPrice,
  hasVariantPricing,
  isAkustikThicknessSelectable,
} from '@/data/productSpecs';
import { ProductSEO } from '@/seo/ProductSEO';
import { useCart } from '@/context/cart';
import { useToast } from '@/components/Toast/Toast';
import { IconArrowRight, IconCart, IconClose, IconDoc, IconInstagram, IconPhone, IconStar, IconWhatsApp } from '@/components/Icons/Icons';
import { buildWhatsAppUrl, SALES_PHONE_READABLE, SUPPORT_PHONE_READABLE } from '@/utils/whatsapp';
import { Accordion } from '@/components/Accordion/Accordion';
import { Tabs } from '@/components/Tabs/Tabs';

const BondexSungerHubContent = dynamic(() => import('@/views/content/BondexSungerHubContent').then((m) => m.BondexSungerHubContent));
const SungerpanHubContent = dynamic(() => import('@/views/content/SungerpanHubContent').then((m) => m.SungerpanHubContent));
const Bondex150DnsHubContent = dynamic(() => import('@/views/content/Bondex150DnsHubContent').then((m) => m.Bondex150DnsHubContent));
const AkustikSungerHubContent = dynamic(() => import('@/views/content/AkustikSungerHubContent').then((m) => m.AkustikSungerHubContent));
const AkustikPiramitSungerHubContent = dynamic(
  () => import('@/views/content/AkustikPiramitSungerHubContent').then((m) => m.AkustikPiramitSungerHubContent)
);
const AkustikLabirentSungerHubContent = dynamic(
  () => import('@/views/content/AkustikLabirentSungerHubContent').then((m) => m.AkustikLabirentSungerHubContent)
);
const AkustikYumurtaSungerHubContent = dynamic(
  () => import('@/views/content/AkustikYumurtaSungerHubContent').then((m) => m.AkustikYumurtaSungerHubContent)
);
const AkustikDuzSungerHubContent = dynamic(
  () => import('@/views/content/AkustikDuzSungerHubContent').then((m) => m.AkustikDuzSungerHubContent)
);

export function ProductDetail() {
  const loc = useLocation();
  const product = getProductByPathname(loc.pathname);
  const cart = useCart();
  const toast = useToast();
  const productLine = product ? getProductLine(product.category) : null;
  const [selectedDensity, setSelectedDensity] = useState<DensityDns>(120);
  const [bariyerliVariantType, setBariyerliVariantType] = useState<'tek' | 'cift'>('tek');
  const [selectedThickness, setSelectedThickness] = useState<number>(() => {
    if (product?.thicknessMm) return product.thicknessMm;
    if (product && isAkustikThicknessSelectable(product.category)) {
      return getDefaultAkustikThickness(product.slug);
    }
    const line = product ? getProductLine(product.category) : null;
    if (line) return getDefaultThickness(line);
    return 10;
  });

  if (!product) {
    return (
      <main className="container" style={{ padding: '28px 0 56px' }}>
        <h1>Ürün bulunamadı</h1>
        <p style={{ color: 'var(--muted)' }}>
          Ürün listesine dönmek için <Link to="/urunler">tıklayın</Link>.
        </p>
      </main>
    );
  }

  const related = getRelatedProducts(product, 6).filter((p) =>
    product.slug === 'sungerpan' ? p.slug !== 'bondex-sunger-40mm' && p.slug !== 'bondex-sunger-50mm' : true
  );
  const isBariyerliHubPage = product.slug === 'bariyerli-bondex-sunger';
  const isThicknessSelectable =
    product.slug === 'bondex-sunger' ||
    product.slug === 'cift-bariyerli-bondex-sunger' ||
    product.slug === 'sungerpan' ||
    product.slug === '150-dns-bondex-sunger';
  const isAkustikSelectable = isAkustikThicknessSelectable(product.category);
  const showPurchaseHighlights = product.category !== 'Akustik Sünger';
  const thicknessOptions = productLine ? PRODUCT_LINE_THICKNESSES[productLine] : [];
  const akustikThicknessOptions = isAkustikSelectable ? getAkustikThicknessOptions(product.slug) : [];
  const tekBariyerliThicknessOptions = PRODUCT_LINE_THICKNESSES.bariyerli;
  const ciftBariyerliThicknessOptions = PRODUCT_LINE_THICKNESSES.cift;
  const bondexThicknessProducts = getProductsByCategory('Bondex Sünger').filter((p) => typeof p.thicknessMm === 'number');
  const bariyerliThicknessProducts = getProductsByCategory('Bariyerli Bondex').filter((p) => typeof p.thicknessMm === 'number');
  const ciftThicknessProducts = getProductsByCategory('Çift Bariyerli Bondex').filter((p) => typeof p.thicknessMm === 'number');

  const activeVariantLine: ProductLine | null = isBariyerliHubPage
    ? bariyerliVariantType === 'tek'
      ? 'bariyerli'
      : 'cift'
    : productLine;
  const specsCategory =
    isBariyerliHubPage && bariyerliVariantType === 'cift' ? 'Çift Bariyerli Bondex' : product.category;

  const activeThicknessMm =
    isThicknessSelectable || isBariyerliHubPage || isAkustikSelectable
      ? selectedThickness
      : product.thicknessMm;
  const variantPrice =
    activeVariantLine && activeThicknessMm
      ? getSalesPrice(activeVariantLine, activeThicknessMm, selectedDensity)
      : undefined;
  const akustikPrice =
    isAkustikSelectable && activeThicknessMm
      ? getAkustikPrice(product.slug, activeThicknessMm)
      : undefined;

  const thicknessVariantProduct = isBariyerliHubPage
    ? (bariyerliVariantType === 'tek'
        ? bariyerliThicknessProducts.find((p) => p.thicknessMm === selectedThickness)
        : ciftThicknessProducts.find((p) => p.thicknessMm === selectedThickness))
    : product.slug === 'bondex-sunger' ||
        product.slug === 'sungerpan' ||
        product.slug === '150-dns-bondex-sunger'
      ? bondexThicknessProducts.find((p) => p.thicknessMm === selectedThickness)
      : product.slug === 'cift-bariyerli-bondex-sunger'
        ? ciftThicknessProducts.find((p) => p.thicknessMm === selectedThickness)
        : undefined;

  const selectedFallbackProduct: Product | undefined =
    isThicknessSelectable || isBariyerliHubPage
      ? {
          ...product,
          ...thicknessVariantProduct,
          category: specsCategory,
          name:
            product.slug === 'sungerpan'
              ? `Süngerpan ${selectedThickness}mm`
              : product.slug === '150-dns-bondex-sunger'
                ? `150 DNS Bondex Sünger ${selectedThickness}mm`
                : activeVariantLine === 'bondex'
                  ? `Bondex Sünger ${selectedThickness}mm`
                  : activeVariantLine === 'bariyerli'
                    ? `Bariyerli Bondex Sünger ${selectedThickness}mm`
                    : `Çift Bariyerli Bondex ${selectedThickness}mm`,
          slug:
            activeVariantLine === 'bondex'
              ? `bondex-sunger-${selectedThickness}mm`
              : activeVariantLine === 'bariyerli'
                ? `bariyerli-bondex-sunger-${selectedThickness}mm`
                : `cift-bariyerli-bondex-sunger-${selectedThickness}mm`,
          thicknessMm: selectedThickness,
          priceTRY: variantPrice,
          priceUnit: 'm2',
        }
      : undefined;

  const activeProduct: Product = {
    ...(selectedFallbackProduct ?? product),
    ...(activeThicknessMm ? { thicknessMm: activeThicknessMm } : {}),
    ...(variantPrice !== undefined ? { priceTRY: variantPrice } : {}),
    ...(akustikPrice !== undefined ? { priceTRY: akustikPrice } : {}),
  };

  const seoProduct: Product =
    isThicknessSelectable || isBariyerliHubPage || isAkustikSelectable
      ? {
          ...activeProduct,
          slug: product.slug,
          name: product.name,
          description: product.description,
          imagePath: activeProduct.imagePath,
          faqs: product.faqs,
          reviews: product.reviews,
        }
      : activeProduct;

  const waMessage = hasVariantPricing(product.category)
    ? `Merhaba, ${activeProduct.name} (${selectedDensity} dns) için fiyat ve teslimat bilgisi rica ediyorum. (${activeProduct.slug})`
    : isAkustikSelectable
      ? `Merhaba, ${activeProduct.name} ${selectedThickness}mm için fiyat ve teslimat bilgisi rica ediyorum. (${activeProduct.slug})`
      : `Merhaba, ${activeProduct.name} için fiyat ve teslimat bilgisi rica ediyorum. (${activeProduct.slug})`;
  const waUrl = buildWhatsAppUrl(waMessage);

  const displayModel =
    product.slug === 'bondex-sunger'
      ? 'B-01'
      : product.slug === 'akustik-sunger'
        ? 'A-01'
        : product.slug === 'akustik-piramit-sunger'
          ? 'A-02'
          : product.slug === 'akustik-labirent-sunger'
            ? 'A-03'
            : product.slug === 'akustik-yumurta-sunger'
              ? 'A-04'
              : product.slug === 'akustik-duz-sunger'
                ? 'A-05'
                : product.slug;

  // Format price if available
  const formatPrice = (value: number) => {
    try {
      return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(value);
    } catch {
      return `${value} TL`;
    }
  };
  const displayPrice = activeProduct.priceTRY ? formatPrice(activeProduct.priceTRY) : null;
  const activeGallery = activeProduct.imageGallery?.length
    ? activeProduct.imageGallery
    : [activeProduct.imagePath];
  const [selectedImage, setSelectedImage] = useState(activeGallery[0]);
  const selectedImageIndex = Math.max(0, activeGallery.findIndex((img) => img === selectedImage));
  const [reviewIndex, setReviewIndex] = useState(0);
  const [reviewsPerView, setReviewsPerView] = useState(4);
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const lightboxScrollYRef = useRef(0);
  const lightboxTriggerRef = useRef<HTMLButtonElement | null>(null);

  const applicationGalleryImages = Array.from({ length: 12 }, (_, index) => `/images/uyg-${index + 1}.webp`);

  const closeLightbox = useCallback(() => {
    const trigger = lightboxTriggerRef.current;
    setLightboxImage(null);
    requestAnimationFrame(() => {
      trigger?.focus({ preventScroll: true });
    });
  }, []);

  const openLightbox = (src: string, trigger: HTMLButtonElement) => {
    lightboxScrollYRef.current = window.scrollY;
    lightboxTriggerRef.current = trigger;
    setLightboxImage(src);
  };

  useEffect(() => {
    if (!lightboxImage) return;

    const scrollY = lightboxScrollYRef.current;
    const bodyStyle = document.body.style;

    bodyStyle.position = 'fixed';
    bodyStyle.top = `-${scrollY}px`;
    bodyStyle.left = '0';
    bodyStyle.right = '0';
    bodyStyle.width = '100%';
    bodyStyle.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeLightbox();
    };

    window.addEventListener('keydown', onKeyDown);

    return () => {
      bodyStyle.position = '';
      bodyStyle.top = '';
      bodyStyle.left = '';
      bodyStyle.right = '';
      bodyStyle.width = '';
      bodyStyle.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
      window.scrollTo(0, scrollY);
    };
  }, [lightboxImage, closeLightbox]);

  useEffect(() => {
    setSelectedImage(activeGallery[0]);
  }, [activeProduct.slug, activeGallery]);

  useEffect(() => {
    if (product.slug === 'bariyerli-bondex-sunger') {
      setBariyerliVariantType('tek');
      setSelectedThickness(13);
      return;
    }
    if (isAkustikThicknessSelectable(product.category)) {
      setSelectedThickness(getDefaultAkustikThickness(product.slug));
      return;
    }
    if (product.thicknessMm) {
      setSelectedThickness(product.thicknessMm);
    } else if (isThicknessSelectable && productLine) {
      setSelectedThickness(getDefaultThickness(productLine));
    }
  }, [product.slug, product.thicknessMm, isThicknessSelectable, productLine]);

  useEffect(() => {
    setSelectedDensity(product.slug === '150-dns-bondex-sunger' ? 150 : 120);
  }, [product.slug]);

  useEffect(() => {
    const updateReviewsPerView = () => {
      if (window.innerWidth < 760) {
        setReviewsPerView(1);
      } else if (window.innerWidth < 1120) {
        setReviewsPerView(2);
      } else {
        setReviewsPerView(4);
      }
    };

    updateReviewsPerView();
    window.addEventListener('resize', updateReviewsPerView);
    return () => window.removeEventListener('resize', updateReviewsPerView);
  }, []);

  const handlePrevImage = () => {
    const nextIndex = (selectedImageIndex - 1 + activeGallery.length) % activeGallery.length;
    setSelectedImage(activeGallery[nextIndex]);
  };

  const handleNextImage = () => {
    const nextIndex = (selectedImageIndex + 1) % activeGallery.length;
    setSelectedImage(activeGallery[nextIndex]);
  };

  const reviews = [
    {
      name: 'Proje yöneticisi',
      text: 'Doğru kalınlığı hızlıca seçtik, teslimat süreci net ve sorunsuz ilerledi.',
    },
    {
      name: 'Uygulama ekibi',
      text: 'Malzeme kalitesi ve bilgilendirme iyi. Bariyerli alternatifleri de karşılaştırabildik.',
    },
    {
      name: 'B2B müşteri',
      text: 'WhatsApp üzerinden teklif aldık, iletişim hızlıydı. Ürün sayfaları açıklayıcı.',
    },
  ];
  const pageReviews = product.reviews?.length
    ? product.reviews.map((r) => ({
      name: r.authorName,
      date: r.datePublished,
      title: r.title,
      verified: !!r.verified,
      rating: r.ratingValue,
      text: r.body,
    }))
    : reviews.map((r) => ({ name: r.name, date: '2025-12-17', title: 'Mükemmel', verified: false, rating: 5, text: r.text }));

  const avgRating =
    pageReviews.length > 0 ? pageReviews.reduce((s, r) => s + (Number.isFinite(r.rating) ? r.rating : 0), 0) / pageReviews.length : 0;
  const maxReviewIndex = Math.max(0, pageReviews.length - reviewsPerView);

  useEffect(() => {
    setReviewIndex((prev) => Math.min(prev, maxReviewIndex));
  }, [maxReviewIndex]);

  const topSpecs = buildTopSpecs(activeThicknessMm, specsCategory, product.slug);
  const highlightSpecs = getHighlightSpecs(topSpecs);
  const remainingSpecs = topSpecs.filter((s) => !highlightSpecs.some((h) => h.k === s.k));

  const usageAreas = [
    { image: '/images/Studyo-muzik-odasi.webp', label: 'Stüdyo ve müzik odası uygulamaları' },
    { image: '/images/makine-dairesi.webp', label: 'Makine dairesi ve jeneratör odası izolasyonu' },
    { image: '/images/duvar.webp', label: 'Duvar ses yalıtımı projeleri' },
    { image: '/images/tavan-ses-yalitimi.webp', label: 'Tavan ses yalıtımı uygulamaları' },
    { image: '/images/zemin.webp', label: 'Zemin altı şap ve kaplama destek çözümleri' },
    { image: '/images/kapi-bolme-duvar.webp', label: 'Kapı ve bölme duvar detaylarında ses kontrolü' },
    { image: '/images/toplanti-odasi.webp', label: 'Ofis ve toplantı odası akustik düzenlemeleri' },
    { image: '/images/sinema-odasi.webp', label: 'Sinema ve ev sineması odaları' },
    { image: '/images/otel.webp', label: 'Otel, pansiyon ve konaklama tesisleri' },
    { image: '/images/spor-salonu.webp', label: 'Spor salonu ve fitness alanları' },
    { image: '/images/endustiryel-tesis.webp', label: 'Endüstriyel tesis ve üretim hatları' },
    { image: '/images/apartman-ve-konut-odasi.webp', label: 'Apartman ve konut arası ses geçiş önleme' },
  ];

  const specGridContent = (
    <div className={styles.specGrid} role="group" aria-label="Teknik özellikler">
      {(showPurchaseHighlights ? [...highlightSpecs, ...remainingSpecs] : topSpecs).map((s) => (
        <div key={s.k} className={styles.specItem}>
          <div className={styles.specK}>{s.k}</div>
          <div className={styles.specV}>{s.v}</div>
        </div>
      ))}
    </div>
  );

  const tabItems = [
    {
      key: 'aciklama',
      label: 'Detaylı Açıklama',
      content: (
        <>
          {product.slug !== 'bondex-sunger' &&
            product.slug !== 'sungerpan' &&
            product.slug !== '150-dns-bondex-sunger' &&
            product.slug !== 'akustik-sunger' &&
            product.slug !== 'akustik-piramit-sunger' &&
            product.slug !== 'akustik-labirent-sunger' &&
            product.slug !== 'akustik-yumurta-sunger' &&
            product.slug !== 'akustik-duz-sunger' && (
            <h2 className={styles.tabH2}>{product.name} hakkında</h2>
          )}
          {product.slug === 'bondex-sunger' ? (
            <BondexSungerHubContent />
          ) : product.slug === 'sungerpan' ? (
            <SungerpanHubContent />
          ) : product.slug === '150-dns-bondex-sunger' ? (
            <Bondex150DnsHubContent />
          ) : product.slug === 'akustik-sunger' ? (
            <AkustikSungerHubContent />
          ) : product.slug === 'akustik-piramit-sunger' ? (
            <AkustikPiramitSungerHubContent />
          ) : product.slug === 'akustik-labirent-sunger' ? (
            <AkustikLabirentSungerHubContent />
          ) : product.slug === 'akustik-yumurta-sunger' ? (
            <AkustikYumurtaSungerHubContent />
          ) : product.slug === 'akustik-duz-sunger' ? (
            <AkustikDuzSungerHubContent />
          ) : product.slug === 'bondex-sunger-10mm' ? (
            <div className={styles.seoWrap}>
              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>1</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    🎯
                  </span>
                  <h3 className={styles.seoTitle}>Bondex sünger 10mm nedir?</h3>
                </header>
                <p className={styles.seoText}>
                  <strong>Bondex sünger 10mm</strong>; rebonded süngerpan teknolojisiyle üretilen, 10 milimetre kalınlığında{' '}
                  <strong>yüksek yoğunluklu</strong> bir ses yalıtım malzemesidir. İnce detaylar, kaplama altı uygulamalar ve
                  sınırlı alanlarda yer tasarrufu sağlarken temel ses yalıtım performansı sunar. <strong>Bondex sünger</strong>{' '}
                  ailesinin en ince kalınlığı olarak, zemin altı şap ve kaplama detaylarında pratik bir çözümdür.
                </p>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>2</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    📍
                  </span>
                  <h3 className={styles.seoTitle}>Bondex sünger 10mm kullanım alanları</h3>
                </header>
                <p className={styles.seoText}>
                  Bondex sünger 10mm; yer tasarrufu gerektiren ve ince detay öncelikli projelerde tercih edilir. Kaplama altı
                  uygulamalar, zemin altı şap detayları ve sınırlı yükseklik olan alanlarda ideal performans sunar.
                </p>
                <ul className={styles.seoList}>
                  <li>Zemin altı şap ve kaplama altı uygulamalarında ince yalıtım katmanı</li>
                  <li>Sınırlı yükseklik olan alanlarda yer tasarrufu sağlayan çözüm</li>
                  <li>İnce detay gerektiren kapı ve duvar uygulamaları</li>
                  <li>Hafif ve orta seviye ses yalıtım ihtiyaçları için pratik seçenek</li>
                </ul>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>3</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    ⚙️
                  </span>
                  <h3 className={styles.seoTitle}>Bondex sünger 10mm özellikleri</h3>
                </header>
                <p className={styles.seoText}>
                  Bondex sünger 10mm seçiminde <strong>kalınlık</strong>, <strong>uygulama yüzeyi</strong> ve{' '}
                  <strong>hedef izolasyon seviyesi</strong> kritik rol oynar. İnce kalınlığı sayesinde yer kaybı olmadan temel
                  ses yalıtımı sağlar; daha yüksek performans gerektiren projelerde kalın modeller değerlendirilebilir.
                </p>
                <ul className={styles.seoList}>
                  <li>10mm ince kalınlık ile yer tasarrufu sağlar</li>
                  <li>Yüksek yoğunluklu rebonded yapı ile darbe ve titreşim etkisini azaltmaya yardımcı olur</li>
                  <li>
                    Daha yüksek izolasyon için{' '}
                    <Link to="/bondex-sunger-20mm" rel="follow">
                      20mm
                    </Link>
                    ,{' '}
                    <Link to="/bondex-sunger-30mm" rel="follow">
                      30mm
                    </Link>{' '}
                    veya{' '}
                    <Link to="/" rel="follow">
                      Bariyerli Bondex Sünger
                    </Link>{' '}
                    alternatifleri tercih edilebilir
                  </li>
                  <li>
                    Farklı <strong>Bondex sünger</strong> kalınlıkları için{' '}
                    <Link to="/bondex-sunger" rel="follow">
                      Bondex sünger
                    </Link>{' '}
                    sayfasından seçim yapılabilir
                  </li>
                </ul>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>4</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    💰
                  </span>
                  <h3 className={styles.seoTitle}>Bondex sünger 10mm fiyatları</h3>
                </header>
                <p className={styles.seoText}>
                  <strong>Bondex sünger 10mm fiyatları</strong>; metraj, plaka ölçüsü ve proje adedine göre değişir.
                  Bondex sünger 10mm için en doğru teklif almak üzere uygulama alanınızı (metraj), hedef izolasyon seviyenizi
                  ve proje detaylarınızı paylaşmanız yeterlidir. Hızlı fiyat bilgisi için WhatsApp üzerinden iletişime
                  geçebilirsiniz. Diğer kalınlıklar için <Link to="/bondex-sunger" rel="follow">Bondex sünger</Link> sayfasını inceleyin.
                </p>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>5</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    💡
                  </span>
                  <h3 className={styles.seoTitle}>Bondex sünger 10mm seçerken nelere dikkat edilmeli?</h3>
                </header>
                <p className={styles.seoText}>
                  Bondex sünger 10mm seçimi; uygulama alanı, hedef izolasyon seviyesi ve mevcut yükseklik kısıtlarına göre
                  yapılmalıdır. İnce kalınlığı sayesinde yer tasarrufu sağlar ancak daha yüksek izolasyon hedeflerinde{' '}
                  <Link to="/bondex-sunger-20mm" rel="follow">
                    20mm
                  </Link>
                  ,{' '}
                  <Link to="/bondex-sunger-30mm" rel="follow">
                    30mm
                  </Link>{' '}
                  veya{' '}
                  <Link to="/" rel="follow">
                    Bariyerli Bondex Sünger
                  </Link>{' '}
                  çözümleri değerlendirilebilir. Klasik <strong>Bondex sünger</strong> uygulamaları için{' '}
                  <Link to="/bondex-sunger" rel="follow">
                    farklı kalınlıklar
                  </Link>{' '}
                  arasından seçim yapılabilir.
                </p>
              </section>
            </div>
          ) : product.slug === 'bondex-sunger-20mm' ? (
            <div className={styles.seoWrap}>
              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>1</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    🎯
                  </span>
                  <h3 className={styles.seoTitle}>Bondex sünger 20mm nedir?</h3>
                </header>
                <p className={styles.seoText}>
                  <strong>Bondex sünger 20mm</strong>; rebonded süngerpan teknolojisiyle üretilen, 20 milimetre kalınlığında{' '}
                  <strong>yüksek yoğunluklu</strong> bir ses yalıtım malzemesidir. Performans ve yer tasarrufu arasında{' '}
                  <strong>dengeli bir seçenek</strong> sunar. <strong>Bondex sünger</strong> ailesinin popüler kalınlığı olarak,{' '}
                  stüdyo, ofis, makine dairesi ve kapı/duvar uygulamalarında sık tercih edilir. Ses yalıtımı ve titreşim sönümleme{' '}
                  projelerinde ideal performans sağlar.
                </p>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>2</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    📍
                  </span>
                  <h3 className={styles.seoTitle}>Bondex sünger 20mm kullanım alanları</h3>
                </header>
                <p className={styles.seoText}>
                  Bondex sünger 20mm; performans ve yer tasarrufu dengesi gerektiren projelerde tercih edilir. Stüdyo, ofis,{' '}
                  makine dairesi ve kapı/duvar uygulamalarında ideal performans sunar.
                </p>
                <ul className={styles.seoList}>
                  <li>Stüdyo, müzik odası ve kayıt odası ses yalıtımı için dengeli çözüm</li>
                  <li>Ofis ortamlarında hava kaynaklı ses geçişini azaltmaya yönelik uygulamalar</li>
                  <li>Jeneratör odası, makine dairesi ve ekipman odalarında titreşim sönümleme</li>
                  <li>Kapı/duvar detaylarında darbe sesini azaltmaya yönelik katmanlı uygulamalar</li>
                  <li>Zemin altı şap ve kaplama altında destekleyici yalıtım katmanı</li>
                </ul>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>3</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    ⚙️
                  </span>
                  <h3 className={styles.seoTitle}>Bondex sünger 20mm özellikleri</h3>
                </header>
                <p className={styles.seoText}>
                  Bondex sünger 20mm seçiminde <strong>kalınlık</strong>, <strong>uygulama yüzeyi</strong> ve{' '}
                  <strong>hedef izolasyon seviyesi</strong> kritik rol oynar. 20mm kalınlık, performans ve yer tasarrufu arasında{' '}
                  optimal denge sağlar; daha yüksek performans gerektiren projelerde kalın modeller değerlendirilebilir.
                </p>
                <ul className={styles.seoList}>
                  <li>20mm dengeli kalınlık ile performans ve yer tasarrufu sağlar</li>
                  <li>Yüksek yoğunluklu rebonded yapı ile darbe ve titreşim etkisini azaltmaya yardımcı olur</li>
                  <li>
                    Daha yüksek izolasyon için{' '}
                    <Link to="/bondex-sunger-30mm" rel="follow">
                      30mm
                    </Link>
                    ,{' '}
                    <Link to="/bondex-sunger-40mm" rel="follow">
                      40mm
                    </Link>
                    ,{' '}
                    <Link to="/bondex-sunger-50mm" rel="follow">
                      50mm
                    </Link>{' '}
                    veya{' '}
                    <Link to="/" rel="follow">
                      Bariyerli Bondex Sünger
                    </Link>{' '}
                    alternatifleri tercih edilebilir
                  </li>
                  <li>
                    İnce detaylar için{' '}
                    <Link to="/bondex-sunger-10mm" rel="follow">
                      10mm
                    </Link>
                    , farklı <strong>Bondex sünger</strong> kalınlıkları için{' '}
                    <Link to="/bondex-sunger" rel="follow">
                      Bondex sünger
                    </Link>{' '}
                    sayfasından seçim yapılabilir
                  </li>
                </ul>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>4</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    💰
                  </span>
                  <h3 className={styles.seoTitle}>Bondex sünger 20mm fiyatları</h3>
                </header>
                <p className={styles.seoText}>
                  <strong>Bondex sünger 20mm fiyatları</strong>; metraj, plaka ölçüsü ve proje adedine göre değişir.
                  Bondex sünger 20mm için en doğru teklif almak üzere uygulama alanınızı (metraj), hedef izolasyon seviyenizi
                  ve proje detaylarınızı paylaşmanız yeterlidir. Hızlı fiyat bilgisi için WhatsApp üzerinden iletişime
                  geçebilirsiniz. Diğer kalınlıklar için <Link to="/bondex-sunger" rel="follow">Bondex sünger</Link> sayfasını inceleyin.
                </p>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>5</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    💡
                  </span>
                  <h3 className={styles.seoTitle}>Bondex sünger 20mm seçerken nelere dikkat edilmeli?</h3>
                </header>
                <p className={styles.seoText}>
                  Bondex sünger 20mm seçimi; uygulama alanı, hedef izolasyon seviyesi ve mevcut yükseklik kısıtlarına göre
                  yapılmalıdır. 20mm kalınlık performans ve yer tasarrufu dengesi gerektiren projeler için idealdir. Daha yüksek izolasyon hedeflerinde{' '}
                  <Link to="/bondex-sunger-30mm" rel="follow">
                    30mm
                  </Link>
                  ,{' '}
                  <Link to="/bondex-sunger-40mm" rel="follow">
                    40mm
                  </Link>
                  ,{' '}
                  <Link to="/bondex-sunger-50mm" rel="follow">
                    50mm
                  </Link>{' '}
                  veya{' '}
                  <Link to="/" rel="follow">
                    Bariyerli Bondex Sünger
                  </Link>{' '}
                  çözümleri değerlendirilebilir. İnce detaylar için{' '}
                  <Link to="/bondex-sunger-10mm" rel="follow">
                    10mm
                  </Link>
                  , klasik <strong>Bondex sünger</strong> uygulamaları için{' '}
                  <Link to="/bondex-sunger" rel="follow">
                    farklı kalınlıklar
                  </Link>{' '}
                  arasından seçim yapılabilir.
                </p>
              </section>
            </div>
          ) : product.slug === 'bondex-sunger-30mm' ? (
            <div className={styles.seoWrap}>
              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>1</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    🎯
                  </span>
                  <h3 className={styles.seoTitle}>Bondex sünger 30mm nedir?</h3>
                </header>
                <p className={styles.seoText}>
                  <strong>Bondex sünger 30mm</strong>; rebonded süngerpan teknolojisiyle üretilen, 30 milimetre kalınlığında{' '}
                  <strong>yüksek yoğunluklu</strong> bir <strong>ses yalıtım süngeridir</strong>. Daha yüksek sönümleme ihtiyacı olan alanlarda{' '}
                  güçlü performans sunar. <strong>Ses izolasyon</strong> ve <strong>akustik sünger</strong> çözümleri için ideal bir seçenektir.{' '}
                  <strong>Bondex sünger</strong> ailesinin güçlü kalınlığı olarak, stüdyo, jeneratör odası, makine dairesi ve{' '}
                  <strong>duvarlara ses yalıtımı</strong> uygulamalarında tercih edilir.
                </p>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>2</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    📍
                  </span>
                  <h3 className={styles.seoTitle}>Bondex sünger 30mm kullanım alanları</h3>
                </header>
                <p className={styles.seoText}>
                  Bondex sünger 30mm; yüksek ses izolasyon gerektiren projelerde tercih edilir. Stüdyo, jeneratör odası, makine dairesi ve{' '}
                  duvarlara ses yalıtımı uygulamalarında ideal performans sunar.
                </p>
                <ul className={styles.seoList}>
                  <li>Stüdyo, müzik odası ve kayıt odası ses yalıtımı için güçlü çözüm</li>
                  <li>Jeneratör odası, makine dairesi ve ekipman odalarında titreşim sönümleme</li>
                  <li>Duvarlara ses yalıtımı uygulamalarında hava kaynaklı ses geçişini azaltmaya yönelik uygulamalar</li>
                  <li>Kapı/duvar detaylarında darbe sesini azaltmaya yönelik katmanlı uygulamalar</li>
                  <li>Akustik sünger ihtiyacı olan alanlarda ses izolasyon performansı</li>
                </ul>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>3</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    ⚙️
                  </span>
                  <h3 className={styles.seoTitle}>Bondex sünger 30mm özellikleri</h3>
                </header>
                <p className={styles.seoText}>
                  Bondex sünger 30mm seçiminde <strong>kalınlık</strong>, <strong>uygulama yüzeyi</strong> ve{' '}
                  <strong>hedef izolasyon seviyesi</strong> kritik rol oynar. 30mm kalınlık, ses izolasyon ve titreşim sönümleme projelerinde{' '}
                  güçlü performans sağlar; daha yüksek performans gerektiren projelerde kalın modeller değerlendirilebilir.
                </p>
                <ul className={styles.seoList}>
                  <li>30mm güçlü kalınlık ile yüksek ses yalıtım performansı sağlar</li>
                  <li>Yüksek yoğunluklu rebonded yapı ile darbe ve titreşim etkisini azaltmaya yardımcı olur</li>
                  <li>
                    Daha yüksek izolasyon için{' '}
                    <Link to="/bondex-sunger-40mm" rel="follow">
                      40mm
                    </Link>
                    ,{' '}
                    <Link to="/bondex-sunger-50mm" rel="follow">
                      50mm
                    </Link>{' '}
                    veya{' '}
                    <Link to="/" rel="follow">
                      Bariyerli Bondex Sünger ses yalıtım süngeri
                    </Link>{' '}
                    alternatifleri tercih edilebilir
                  </li>
                  <li>
                    Orta seviye ihtiyaçlar için{' '}
                    <Link to="/bondex-sunger-20mm" rel="follow">
                      20mm
                    </Link>
                    , ince detaylar için{' '}
                    <Link to="/bondex-sunger-10mm" rel="follow">
                      10mm
                    </Link>
                    , farklı <strong>Bondex sünger</strong> kalınlıkları için{' '}
                    <Link to="/bondex-sunger" rel="follow">
                      Bondex sünger
                    </Link>{' '}
                    sayfasından seçim yapılabilir
                  </li>
                </ul>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>4</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    💰
                  </span>
                  <h3 className={styles.seoTitle}>Bondex sünger 30mm fiyatları</h3>
                </header>
                <p className={styles.seoText}>
                  <strong>Bondex sünger 30mm fiyatları</strong>; metraj, plaka ölçüsü ve proje adedine göre değişir.
                  Bondex sünger 30mm için en doğru teklif almak üzere uygulama alanınızı (metraj), hedef izolasyon seviyenizi
                  ve proje detaylarınızı paylaşmanız yeterlidir. <strong>Ses yalıtım fiyatları</strong> hakkında hızlı bilgi için WhatsApp üzerinden iletişime
                  geçebilirsiniz. Diğer kalınlıklar için <Link to="/bondex-sunger" rel="follow">Bondex sünger</Link> sayfasını inceleyin.
                </p>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>5</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    💡
                  </span>
                  <h3 className={styles.seoTitle}>Bondex sünger 30mm seçerken nelere dikkat edilmeli?</h3>
                </header>
                <p className={styles.seoText}>
                  Bondex sünger 30mm seçimi; uygulama alanı, hedef izolasyon seviyesi ve mevcut yükseklik kısıtlarına göre
                  yapılmalıdır. 30mm kalınlık yüksek ses izolasyon gerektiren projeler için idealdir. Daha yüksek izolasyon hedeflerinde{' '}
                  <Link to="/bondex-sunger-40mm" rel="follow">
                    40mm
                  </Link>
                  ,{' '}
                  <Link to="/bondex-sunger-50mm" rel="follow">
                    50mm
                  </Link>{' '}
                  veya{' '}
                  <Link to="/" rel="follow">
                    Bariyerli Bondex Sünger ses yalıtım süngeri
                  </Link>{' '}
                  çözümleri değerlendirilebilir. Orta seviye ihtiyaçlar için{' '}
                  <Link to="/bondex-sunger-20mm" rel="follow">
                    20mm
                  </Link>
                  , klasik <strong>Bondex sünger</strong> uygulamaları için{' '}
                  <Link to="/bondex-sunger" rel="follow">
                    farklı kalınlıklar
                  </Link>{' '}
                  arasından seçim yapılabilir.
                </p>
              </section>
            </div>
          ) : product.slug === 'bondex-sunger-40mm' ? (
            <div className={styles.seoWrap}>
              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>1</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    🎯
                  </span>
                  <h3 className={styles.seoTitle}>Bondex sünger 40mm nedir?</h3>
                </header>
                <p className={styles.seoText}>
                  <strong>Bondex sünger 40mm</strong>; rebonded süngerpan teknolojisiyle üretilen, 40 milimetre kalınlığında{' '}
                  <strong>yüksek performanslı ses yalıtım süngeridir</strong>. Yoğunluk ve kalınlığın etkisiyle{' '}
                  <strong>ses izolasyon</strong> performansını artırmaya yardımcı olur. <strong>Yalıtım süngeri</strong> olarak yüksek performans gerektiren projelerde tercih edilir.{' '}
                  <strong>Bondex sünger</strong> ailesinin güçlü kalınlığı olarak, jeneratör odası, makine dairesi, stüdyo ve{' '}
                  <strong>duvarlara ses yalıtımı</strong> uygulamalarında premium seçenektir.
                </p>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>2</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    📍
                  </span>
                  <h3 className={styles.seoTitle}>Bondex sünger 40mm kullanım alanları</h3>
                </header>
                <p className={styles.seoText}>
                  Bondex sünger 40mm; yüksek ses izolasyon gerektiren projelerde tercih edilir. Jeneratör odası, makine dairesi, stüdyo ve{' '}
                  duvarlara ses yalıtımı uygulamalarında güçlü performans sunar.
                </p>
                <ul className={styles.seoList}>
                  <li>Jeneratör odası, makine dairesi ve ekipman odalarında yüksek seviye titreşim sönümleme</li>
                  <li>Stüdyo, müzik odası ve kayıt odası ses yalıtımı için premium çözüm</li>
                  <li>Duvarlara ses yalıtımı uygulamalarında hava kaynaklı ses geçişini azaltmaya yönelik uygulamalar</li>
                  <li>Yalıtım süngeri ihtiyacı olan alanlarda ses izolasyon performansı</li>
                  <li>Kapı/duvar detaylarında darbe sesini azaltmaya yönelik katmanlı uygulamalar</li>
                </ul>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>3</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    ⚙️
                  </span>
                  <h3 className={styles.seoTitle}>Bondex sünger 40mm özellikleri</h3>
                </header>
                <p className={styles.seoText}>
                  Bondex sünger 40mm seçiminde <strong>kalınlık</strong>, <strong>uygulama yüzeyi</strong> ve{' '}
                  <strong>hedef izolasyon seviyesi</strong> kritik rol oynar. 40mm kalınlık, ses izolasyon ve titreşim sönümleme projelerinde{' '}
                  yüksek performans sağlar; daha yüksek performans gerektiren projelerde kalın modeller değerlendirilebilir.
                </p>
                <ul className={styles.seoList}>
                  <li>40mm yüksek kalınlık ile premium ses yalıtım performansı sağlar</li>
                  <li>Yüksek yoğunluklu rebonded yapı ile darbe ve titreşim etkisini azaltmaya yardımcı olur</li>
                  <li>
                    Daha yüksek izolasyon için{' '}
                    <Link to="/bondex-sunger-50mm" rel="follow">
                      50mm
                    </Link>{' '}
                    veya{' '}
                    <Link to="/" rel="follow">
                      Bariyerli Bondex Sünger ses yalıtım süngeri
                    </Link>{' '}
                    alternatifleri tercih edilebilir
                  </li>
                  <li>
                    Orta seviye ihtiyaçlar için{' '}
                    <Link to="/bondex-sunger-30mm" rel="follow">
                      30mm
                    </Link>
                    , dengeli seçim için{' '}
                    <Link to="/bondex-sunger-20mm" rel="follow">
                      20mm
                    </Link>
                    , farklı <strong>Bondex sünger</strong> kalınlıkları için{' '}
                    <Link to="/bondex-sunger" rel="follow">
                      Bondex sünger
                    </Link>{' '}
                    sayfasından seçim yapılabilir
                  </li>
                </ul>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>4</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    💰
                  </span>
                  <h3 className={styles.seoTitle}>Bondex sünger 40mm fiyatları</h3>
                </header>
                <p className={styles.seoText}>
                  <strong>Bondex sünger 40mm fiyatları</strong>; metraj, plaka ölçüsü ve proje adedine göre değişir.
                  Bondex sünger 40mm için en doğru teklif almak üzere uygulama alanınızı (metraj), hedef izolasyon seviyenizi
                  ve proje detaylarınızı paylaşmanız yeterlidir. <strong>Ses yalıtım fiyatları</strong> hakkında hızlı bilgi için WhatsApp üzerinden iletişime
                  geçebilirsiniz. Diğer kalınlıklar için <Link to="/bondex-sunger" rel="follow">Bondex sünger</Link> sayfasını inceleyin.
                </p>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>5</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    💡
                  </span>
                  <h3 className={styles.seoTitle}>Bondex sünger 40mm seçerken nelere dikkat edilmeli?</h3>
                </header>
                <p className={styles.seoText}>
                  Bondex sünger 40mm seçimi; uygulama alanı, hedef izolasyon seviyesi ve mevcut yükseklik kısıtlarına göre
                  yapılmalıdır. 40mm kalınlık yüksek performans gerektiren projeler için idealdir. Daha yüksek izolasyon hedeflerinde{' '}
                  <Link to="/bondex-sunger-50mm" rel="follow">
                    50mm
                  </Link>{' '}
                  veya{' '}
                  <Link to="/" rel="follow">
                    Bariyerli Bondex Sünger ses yalıtım süngeri
                  </Link>{' '}
                  çözümleri değerlendirilebilir. Orta seviye ihtiyaçlar için{' '}
                  <Link to="/bondex-sunger-30mm" rel="follow">
                    30mm
                  </Link>
                  , klasik <strong>Bondex sünger</strong> uygulamaları için{' '}
                  <Link to="/bondex-sunger" rel="follow">
                    farklı kalınlıklar
                  </Link>{' '}
                  arasından seçim yapılabilir.
                </p>
              </section>
            </div>
          ) : product.slug === 'bondex-sunger-50mm' ? (
            <div className={styles.seoWrap}>
              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>1</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    🎯
                  </span>
                  <h3 className={styles.seoTitle}>Bondex sünger 50mm nedir?</h3>
                </header>
                <p className={styles.seoText}>
                  <strong>Bondex sünger 50mm</strong>; rebonded süngerpan teknolojisiyle üretilen, 50 milimetre kalınlığında{' '}
                  <strong>maksimum performanslı ses yalıtım süngeridir</strong>. Maksimum kalınlık ile{' '}
                  <strong>ses izolasyon</strong> performansını en üst seviyeye çıkarır. <strong>İzolasyon süngeri</strong> olarak en yüksek performans gerektiren projelerde tercih edilir.{' '}
                  <strong>Bondex sünger</strong> ailesinin en güçlü kalınlığı olarak, jeneratör odası, makine dairesi, ekipman odaları ve{' '}
                  <strong>duvarlara ses yalıtımı</strong> uygulamalarında premium seçenektir.
                </p>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>2</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    📍
                  </span>
                  <h3 className={styles.seoTitle}>Bondex sünger 50mm kullanım alanları</h3>
                </header>
                <p className={styles.seoText}>
                  Bondex sünger 50mm; maksimum ses izolasyon gerektiren projelerde tercih edilir. Jeneratör odası, makine dairesi, ekipman odaları ve{' '}
                  duvarlara ses yalıtımı uygulamalarında en yüksek performansı sunar.
                </p>
                <ul className={styles.seoList}>
                  <li>Jeneratör odası, makine dairesi ve ekipman odalarında maksimum seviye titreşim sönümleme</li>
                  <li>Stüdyo, müzik odası ve kayıt odası ses yalıtımı için premium çözüm</li>
                  <li>Duvarlara ses yalıtımı uygulamalarında maksimum hava kaynaklı ses geçişini azaltmaya yönelik uygulamalar</li>
                  <li>İzolasyon süngeri ihtiyacı olan alanlarda maksimum ses izolasyon performansı</li>
                  <li>Kapı/duvar detaylarında darbe sesini azaltmaya yönelik katmanlı uygulamalar</li>
                </ul>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>3</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    ⚙️
                  </span>
                  <h3 className={styles.seoTitle}>Bondex sünger 50mm özellikleri</h3>
                </header>
                <p className={styles.seoText}>
                  Bondex sünger 50mm seçiminde <strong>kalınlık</strong>, <strong>uygulama yüzeyi</strong> ve{' '}
                  <strong>hedef izolasyon seviyesi</strong> kritik rol oynar. 50mm kalınlık, ses izolasyon ve titreşim sönümleme projelerinde{' '}
                  maksimum performans sağlar; daha yüksek performans gerektiren projelerde Bariyerli Bondex Sünger alternatifleri değerlendirilebilir.
                </p>
                <ul className={styles.seoList}>
                  <li>50mm maksimum kalınlık ile premium ses yalıtım performansı sağlar</li>
                  <li>Yüksek yoğunluklu rebonded yapı ile darbe ve titreşim etkisini azaltmaya yardımcı olur</li>
                  <li>
                    Daha yüksek izolasyon için{' '}
                    <Link to="/" rel="follow">
                      Bariyerli Bondex Sünger ses yalıtım süngeri
                    </Link>{' '}
                    alternatifleri tercih edilebilir
                  </li>
                  <li>
                    Yüksek seviye ihtiyaçlar için{' '}
                    <Link to="/bondex-sunger-40mm" rel="follow">
                      40mm
                    </Link>
                    , orta seviye için{' '}
                    <Link to="/bondex-sunger-30mm" rel="follow">
                      30mm
                    </Link>
                    , farklı <strong>Bondex sünger</strong> kalınlıkları için{' '}
                    <Link to="/bondex-sunger" rel="follow">
                      Bondex sünger
                    </Link>{' '}
                    sayfasından seçim yapılabilir
                  </li>
                </ul>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>4</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    💰
                  </span>
                  <h3 className={styles.seoTitle}>Bondex sünger 50mm fiyatları</h3>
                </header>
                <p className={styles.seoText}>
                  <strong>Bondex sünger 50mm fiyatları</strong>; metraj, plaka ölçüsü ve proje adedine göre değişir.
                  Bondex sünger 50mm için en doğru teklif almak üzere uygulama alanınızı (metraj), hedef izolasyon seviyenizi
                  ve proje detaylarınızı paylaşmanız yeterlidir. <strong>Ses yalıtım fiyatları</strong> hakkında hızlı bilgi için WhatsApp üzerinden iletişime
                  geçebilirsiniz. Diğer kalınlıklar için <Link to="/bondex-sunger" rel="follow">Bondex sünger</Link> sayfasını inceleyin.
                </p>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>5</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    💡
                  </span>
                  <h3 className={styles.seoTitle}>Bondex sünger 50mm seçerken nelere dikkat edilmeli?</h3>
                </header>
                <p className={styles.seoText}>
                  Bondex sünger 50mm seçimi; uygulama alanı, hedef izolasyon seviyesi ve mevcut yükseklik kısıtlarına göre
                  yapılmalıdır. 50mm kalınlık maksimum performans gerektiren projeler için idealdir. Daha yüksek izolasyon hedeflerinde{' '}
                  <Link to="/" rel="follow">
                    Bariyerli Bondex Sünger ses yalıtım süngeri
                  </Link>{' '}
                  çözümleri değerlendirilebilir. Yüksek seviye ihtiyaçlar için{' '}
                  <Link to="/bondex-sunger-40mm" rel="follow">
                    40mm
                  </Link>
                  , klasik <strong>Bondex sünger</strong> uygulamaları için{' '}
                  <Link to="/bondex-sunger" rel="follow">
                    farklı kalınlıklar
                  </Link>{' '}
                  arasından seçim yapılabilir.
                </p>
              </section>
            </div>
          ) : product.slug === 'bariyerli-bondex-sunger' ? (
            <div className={styles.seoWrap}>
              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>1</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    🎯
                  </span>
                  <h3 className={styles.seoTitle}>Bariyerli Bondex Sünger nedir?</h3>
                </header>
                <p className={styles.seoText}>
                  <strong>Bariyerli Bondex Sünger</strong>, <strong>Bondex sünger</strong> ile ağır bariyer katmanının birleştiği{' '}
                  <strong>bariyerli bondex sünger</strong> çözümüdür. Hava kaynaklı ses geçişini azaltmak için kompozit yapıda tasarlanır;{' '}
                  duvar ve kapı ses yalıtımı, makine dairesi ve stüdyo gibi yüksek performans gereken projelerde <strong>yüksek ses yalıtımı</strong> sağlar.{' '}
                  23mm, 33mm, 43mm ve 53mm kalınlık seçenekleriyle projeye göre optimizasyon yapılabilir. Daha yoğun bariyer ihtiyacı için{' '}
                  <Link to="/cift-bariyerli-bondex-sunger" rel="follow">
                    Çift Bariyerli Bondex
                  </Link>{' '}
                  alternatifleri de aynı kategoride değerlendirilir.
                </p>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>2</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    📍
                  </span>
                  <h3 className={styles.seoTitle}>Bariyerli Bondex Sünger kullanım alanları</h3>
                </header>
                <p className={styles.seoText}>
                  <strong>Bariyerli Bondex Sünger</strong>, bariyerli kompozit yapısıyla yüksek izolasyon gerektiren projelerde tercih edilir;{' '}
                  <strong>bariyerli bondex sünger</strong> arayanlar için en yaygın senaryolar aşağıdadır.
                </p>
                <ul className={styles.seoList}>
                  <li>Duvar ve bölme ses yalıtımı: hava kaynaklı ses geçişini azaltan bariyerli çözüm</li>
                  <li>Kapı ses yalıtımı: ağır bariyer katmanı ile kapı kanadında sızdırmazlık destekler</li>
                  <li>Stüdyo, kayıt ve yayın odaları: dış gürültüyü keserken içerideki sesin dışarı çıkmasını sınırlar</li>
                  <li>Makine dairesi, jeneratör odası, ekipman odaları: titreşim + hava yolu sesini birlikte düşürür</li>
                  <li>Konfor & ofis alanları: toplantı odası, ev sinema, açık ofis bölücü uygulamalarında yüksek performanslı bariyerli yalıtım</li>
                </ul>
                <div className={styles.bariyerliVisualRow} role="group" aria-label="Bariyerli Bondex Sünger uygulama görselleri">
                  <figure className={styles.bariyerliVisualCard}>
                    <div className={styles.bariyerliVisualImgWrap}>
                      <img
                        className={styles.bariyerliVisualImg}
                        src="/images/bondex-2.webp"
                        alt="Bariyerli Bondex Sünger duvar ve bölme ses yalıtımı uygulaması"
                        loading="lazy"
                        width={400}
                        height={300}
                      />
                    </div>
                    <figcaption className={styles.bariyerliVisualCap}>Duvar ve bölme ses yalıtımı</figcaption>
                  </figure>
                  <figure className={styles.bariyerliVisualCard}>
                    <div className={styles.bariyerliVisualImgWrap}>
                      <img
                        className={styles.bariyerliVisualImg}
                        src="/images/bondex-3.webp"
                        alt="Bariyerli Bondex Sünger kapı ses yalıtımı uygulaması"
                        loading="lazy"
                        width={400}
                        height={300}
                      />
                    </div>
                    <figcaption className={styles.bariyerliVisualCap}>Kapı ses yalıtımı</figcaption>
                  </figure>
                  <figure className={styles.bariyerliVisualCard}>
                    <div className={styles.bariyerliVisualImgWrap}>
                      <img
                        className={styles.bariyerliVisualImg}
                        src="/images/bondex-4.webp"
                        alt="Bariyerli Bondex Sünger stüdyo ve kayıt odası uygulaması"
                        loading="lazy"
                        width={400}
                        height={300}
                      />
                    </div>
                    <figcaption className={styles.bariyerliVisualCap}>Stüdyo ve kayıt ortamı</figcaption>
                  </figure>
                  <figure className={styles.bariyerliVisualCard}>
                    <div className={styles.bariyerliVisualImgWrap}>
                      <img
                        className={styles.bariyerliVisualImg}
                        src="/images/bondex-5.webp"
                        alt="Bariyerli Bondex Sünger makine dairesi ve ofis uygulaması"
                        loading="lazy"
                        width={400}
                        height={300}
                      />
                    </div>
                    <figcaption className={styles.bariyerliVisualCap}>Makine dairesi ve ofis</figcaption>
                  </figure>
                </div>
              </section>

              <section className={styles.seoCard}>
                <h2 className={styles.seoH2}>Bariyerli Bondex Sünger Kaç dB Ses Keser?</h2>
                <p className={styles.seoText}>
                  <strong>Bariyerli Bondex Sünger</strong> için sabit bir desibel değeri verilmez. Ölçüm, duvar veya kapı
                  sisteminin tamamında yapılır; levha tek başına Rw veya dB olarak raporlanmaz. Bariyer katmanı hava
                  kaynaklı ses geçişini düşürür; rebonded sünger kısmı titreşim ve darbeyi sönümler. Laboratuvar
                  testleri ile şantiye ölçümü arasında fark çıkması normaldir — montaj sürekliliği, boşluklar ve kapı
                  detayları sonucu belirler.
                </p>

                <h3 className={styles.seoSubtitle}>Hava kaynaklı seslerde performansı</h3>
                <p className={styles.seoText}>
                  Konuşma, televizyon, müzik ve klima gürültüsü hava yoluyla taşınır. <strong>Bariyerli bondex sünger</strong>{' '}
                  bu frekans bandında bariyer katmanı sayesinde klasik{' '}
                  <Link to="/bondex-sunger" rel="follow">Bondex sünger</Link>’e göre daha yüksek izolasyon sağlar.
                  Komşu daire duvarı, toplantı odası bölmesi ve ev sinema odası uygulamalarında bariyerli kompozit levha
                  karkas içinde veya doğrudan yüzeye yapıştırılarak kullanılır. Orta ve yüksek frekanslarda bariyer
                  katmanının kütle etkisi belirgindir; düşük frekanslı bas seslerde ek kalınlık veya çift bariyer
                  gerekebilir. Performans; levha ek yerlerinin bantlanması, priz ve tesisat boşluklarının kapatılması,
                  tavan-taban birleşimlerinin sürekliliği ve kapı-pencere zayıf noktalarının giderilmesi ile doğrudan
                  ilişkilidir. Duvarın tamamı bariyerli levha ile kaplansa bile kapıdan sızıntı varsa ölçümde düşük
                  sonuç alınır.
                </p>

                <h3 className={styles.seoSubtitle}>Darbe sesi üzerindeki etkisi</h3>
                <p className={styles.seoText}>
                  Ayak sesi ve yapısal titreşim öncelikle rebonded süngerpan katmanında sönümlenir. Bariyer katmanı darbe
                  için tasarlanmamış olsa da kompozit yapı sayesinde zemin ve ince bölme duvarlarda darbe + hava yolu
                  birlikte ele alınabilir. Yoğun darbe yükü olan zeminlerde bariyerli levha şap altına konur; üstüne şap
                  ve kaplama gelir. Sadece darbe problemi varsa ve komşu konuşma sesi yoksa standart bondex süngerpan
                  yeterli kalabilir; iki problem bir aradaysa bariyerli model tercih edilir.
                </p>

                <h3 className={styles.seoSubtitle}>Tek kat ve çift kat uygulama farkı</h3>
                <p className={styles.seoText}>
                  Tek kat <strong>Bariyerli Bondex Sünger</strong> çoğu konut ve ofis duvarında yeterli seviyeye ulaşır.
                  Çift kat (yüz yüze iki levha veya karşılıklı iki duvar yüzeyi) izolasyonu artırır; ancak hacim kaybı
                  ve maliyet yükselir. Kritik stüdyo, kayıt odası veya yüksek ses seviyeli makine hacimlerinde çift kat
                  veya{' '}
                  <Link to="/cift-bariyerli-bondex-sunger" rel="follow">
                    Çift Bariyerli Bondex Sünger
                  </Link>{' '}
                  değerlendirilir. Çift kat uygulamada bariyer yüzeylerinin aynı hizada örtüşmesi ve ara boşlukta hava
                  hapı bırakılmaması gerekir.
                </p>

                <h3 className={styles.seoSubtitle}>Bariyer kalınlığının performansa etkisi</h3>
                <p className={styles.seoText}>
                  Toplam levha kalınlığı (23, 33, 43, 53 mm) arttıkça hem bariyer hem sünger kısmı kalınlaşır. İnce
                  detay ve kapı için{' '}
                  <Link to="/bariyerli-bondex-sunger-23mm" rel="follow">23mm</Link>, konut duvarı ve ofis için{' '}
                  <Link to="/bariyerli-bondex-sunger-33mm" rel="follow">33mm</Link>, makine dairesi için{' '}
                  <Link to="/bariyerli-bondex-sunger-43mm" rel="follow">43mm</Link>, maksimum hava yolu kesimi için{' '}
                  <Link to="/bariyerli-bondex-sunger-53mm" rel="follow">53mm</Link> kullanılır. Bariyer ağırlığı
                  (kg/m²) arttıkça düşük frekanslı hava kaynaklı seslerde iyileşme görülür; yapısal limit ve kapı
                  kalınlığı seçimi sınırlar.
                </p>
                <div className={styles.bariyerliVisualBreakout}>
                  <div
                    className={styles.bariyerliVisualRow}
                    role="group"
                    aria-label="Bariyerli Bondex kalınlık seçenekleri görselleri"
                  >
                    <figure className={styles.bariyerliVisualCard}>
                      <div className={styles.bariyerliVisualImgWrap}>
                        <img
                          className={styles.bariyerliVisualImg}
                          src="/images/bariyerli-2.webp"
                          alt="Bariyerli Bondex Sünger 23 mm — kapı ve ince detay uygulaması"
                          loading="lazy"
                          width={400}
                          height={300}
                        />
                      </div>
                      <figcaption className={styles.bariyerliVisualCap}>
                        23 mm — kapı ve ince detay
                      </figcaption>
                    </figure>
                    <figure className={styles.bariyerliVisualCard}>
                      <div className={styles.bariyerliVisualImgWrap}>
                        <img
                          className={styles.bariyerliVisualImg}
                          src="/images/bariyerli-3.webp"
                          alt="Bariyerli Bondex Sünger 33 mm — konut duvarı ve ofis uygulaması"
                          loading="lazy"
                          width={400}
                          height={300}
                        />
                      </div>
                      <figcaption className={styles.bariyerliVisualCap}>
                        33 mm — konut duvarı ve ofis
                      </figcaption>
                    </figure>
                    <figure className={styles.bariyerliVisualCard}>
                      <div className={styles.bariyerliVisualImgWrap}>
                        <img
                          className={styles.bariyerliVisualImg}
                          src="/images/bariyerli-4.webp"
                          alt="Bariyerli Bondex Sünger 43 mm — makine dairesi uygulaması"
                          loading="lazy"
                          width={400}
                          height={300}
                        />
                      </div>
                      <figcaption className={styles.bariyerliVisualCap}>43 mm — makine dairesi</figcaption>
                    </figure>
                    <figure className={styles.bariyerliVisualCard}>
                      <div className={styles.bariyerliVisualImgWrap}>
                        <img
                          className={styles.bariyerliVisualImg}
                          src="/images/bariyerli-5.webp"
                          alt="Bariyerli Bondex Sünger 53 mm — maksimum hava yolu kesimi uygulaması"
                          loading="lazy"
                          width={400}
                          height={300}
                        />
                      </div>
                      <figcaption className={styles.bariyerliVisualCap}>
                        53 mm — maksimum hava yolu kesimi
                      </figcaption>
                    </figure>
                  </div>
                </div>
              </section>

              <section className={styles.seoCard}>
                <h2 className={styles.seoH2}>Bariyerli Bondex ile Normal Bondex Arasındaki Fark</h2>
                <p className={styles.seoText}>
                  Normal bondex süngerpan yalnızca rebonded süngerden oluşur. <strong>Bariyerli bondex sünger</strong>{' '}
                  aynı sünger gövdesine eklenmiş ağır, esnek bariyer katmanı içerir. İkisi farklı gürültü tiplerine
                  karşı optimize edilmiştir; biri diğerinin “üst modeli” değil, farklı teknik görevli malzemedir.
                </p>

                <h3 className={styles.seoSubtitle}>Bariyer katmanı ne işe yarar?</h3>
                <p className={styles.seoText}>
                  Bariyer katmanı ses dalgasının geçişini kütle ve esneklik ile zorlaştırır. Hava kaynaklı ses duvar
                  veya kapı yüzeyinden geçerken bariyer katmanında enerji kaybı oluşur. Rebonded sünger bu geçişi
                  destekler; tek başına süngerpan hava yolu sesinde sınırlı kalır. Bariyerli Bondex Sünger’de bariyer
                  yüzeyi ses kaynağına bakacak şekilde monte edilir; ters montaj performansı düşürür.
                </p>

                <h3 className={styles.seoSubtitle}>Hangi projede standart Bondex yeterlidir?</h3>
                <p className={styles.seoText}>
                  Makine altı titreşim, kapı içi hafif sönümleme, zemin darbesi ve akustik düzenleme (yankı kontrolü)
                  ağırlıklı projelerde standart{' '}
                  <Link to="/bondex-sunger" rel="follow">Bondex sünger</Link> yeterlidir. Jeneratör odası zemin
                  altı, spor salonu şap detayı, kapı kasası dolgusu bu gruba girer. Komşu birimden belirgin konuşma
                  veya TV sesi gelmiyorsa bariyerli modele geçmek zorunlu değildir.
                </p>

                <h3 className={styles.seoSubtitle}>Hangi projelerde bariyerli bondex gerekir?</h3>
                <p className={styles.seoText}>
                  Komşu duvar ve tavan ses geçişi, stüdyo dış izolasyonu, ev sinema odası, toplantı odası gizliliği,
                  makine dairesi duvarı ve yüksek ses seviyeli ticari alanlar bariyerli bondex sünger gerektirir.
                  İnce bölme duvarlarda standart bondex eklenmesine rağmen ses geçişi devam ediyorsa bariyer katmanı
                  eksikliği düşünülmelidir. Otel ve hastane gibi yapılarda oda arası ses yalıtımı için bariyerli
                  kompozit levha standart detay haline gelmiştir.
                </p>

                <h3 className={styles.seoSubtitle}>Ses kesme performansı karşılaştırması</h3>
                <p className={styles.seoText}>
                  Aynı kalınlıkta (örneğin 33 mm) standart bondex ile bariyerli bondex karşılaştırıldığında hava
                  kaynaklı seslerde bariyerli model belirgin üstünlük gösterir. Darbe tarafında fark daha az olabilir;
                  her iki ürün de rebonded gövde taşır. Sistem ölçümünde bariyerli levha + alçıpan + karkas üçlüsü,
                  tek kat standart bondex + aynı karkastan daha yüksek Rw değeri verir. Maksimum izolasyon hedefinde{' '}
                  <Link to="/cift-bariyerli-bondex-sunger" rel="follow">çift bariyerli</Link> modeller değerlendirilir.
                </p>
                <div className={styles.bariyerliVisualBreakout}>
                  <div
                    className={styles.bariyerliVisualRow}
                    role="group"
                    aria-label="Bariyerli Bondex ve standart Bondex görsel karşılaştırması"
                  >
                    <figure className={styles.bariyerliVisualCard}>
                      <div className={styles.bariyerliVisualImgWrap}>
                        <img
                          className={styles.bariyerliVisualImg}
                          src="/images/bariyerli-6.webp"
                          alt="Bariyerli Bondex Sünger ürün görünümü"
                          loading="lazy"
                          width={400}
                          height={300}
                        />
                      </div>
                      <figcaption className={styles.bariyerliVisualCap}>Bariyerli Bondex sünger</figcaption>
                    </figure>
                    <figure className={styles.bariyerliVisualCard}>
                      <div className={styles.bariyerliVisualImgWrap}>
                        <img
                          className={styles.bariyerliVisualImg}
                          src="/images/bariyerli-7.webp"
                          alt="Bariyerli Bondex Sünger detay ve uygulama görseli"
                          loading="lazy"
                          width={400}
                          height={300}
                        />
                      </div>
                      <figcaption className={styles.bariyerliVisualCap}>Bariyerli Bondex — detay</figcaption>
                    </figure>
                    <figure className={styles.bariyerliVisualCard}>
                      <div className={styles.bariyerliVisualImgWrap}>
                        <img
                          className={styles.bariyerliVisualImg}
                          src="/images/bondex-6.webp"
                          alt="Standart Bondex süngerpan ürün görünümü"
                          loading="lazy"
                          width={400}
                          height={300}
                        />
                      </div>
                      <figcaption className={styles.bariyerliVisualCap}>Standart Bondex sünger</figcaption>
                    </figure>
                    <figure className={styles.bariyerliVisualCard}>
                      <div className={styles.bariyerliVisualImgWrap}>
                        <img
                          className={styles.bariyerliVisualImg}
                          src="/images/bondex-7.webp"
                          alt="Standart Bondex süngerpan detay görseli"
                          loading="lazy"
                          width={400}
                          height={300}
                        />
                      </div>
                      <figcaption className={styles.bariyerliVisualCap}>Standart Bondex — detay</figcaption>
                    </figure>
                  </div>
                </div>
              </section>

              <section className={styles.seoCard}>
                <h2 className={styles.seoH2}>Bariyerli Bondex Sünger Nasıl Uygulanır?</h2>
                <p className={styles.seoText}>
                  <strong>Bariyerli Bondex Sünger uygulaması</strong> yüzey tipine göre değişir. Ortak kural: yüzey kuru
                  ve temiz olmalı, bariyer katmanı ses kaynağına dönük olmalı, ek yerleri üst üste binmeli ve köşelerde
                  süreklilik sağlanmalıdır.
                </p>

                <h3 className={styles.seoSubtitle}>Duvar uygulama detayı</h3>
                <p className={styles.seoText}>
                  Betonarme duvara doğrudan yapıştırma veya karkas arasına yerleştirme yapılır. Karkas sistemde profil
                  montajından sonra bariyerli levha karkas boşluğunu kaplayacak şekilde kesilir; bariyer yüzü ses
                  kaynağına (komşu taraf veya dış ortam) bakacak şekilde yerleştirilir; üzerine alçıpan vidalanır.
                  Doğrudan yapıştırmada uygun yapıştırıcı tüm yüzeye sürülür; hava kabarcığı rulo ile alınır. Levha ek
                  yerleri en az 50–100 mm bindirmeli ve akustik bantla kapatılmalıdır. Tavan ve taban birleşiminde
                  levha duvar yüzeyine kadar uzatılır; üst ve alt köşede ses sızdıran boşluk kalmamalıdır. İki komşu
                  daire arasında tek tarafa uygulama kısmi fayda sağlar; yüksek izolasyon için her iki tarafın da
                  bariyerli levha ile kaplanması veya ortak duvar kalınlığının artırılması gerekir.
                </p>

                <h3 className={styles.seoSubtitle}>Kapı içi uygulama</h3>
                <p className={styles.seoText}>
                  Kapı kanadı ve kasa çevresi ses yalıtımında ince{' '}
                  <Link to="/bariyerli-bondex-sunger-23mm" rel="follow">23mm</Link> model sık kullanılır. Levha kapı
                  iç yüzeyine yapıştırılır; menteşe ve kilit bölgelerinde kesim hassas yapılmalıdır. Kasa ile duvar
                  birleşiminde bariyer sürekliliği koparsa kapı yalıtımı etkisiz kalır. Kapı altı süpürgelik ve
                  akustik contalar montajın tamamlayıcı parçasıdır; yalnızca sünger levha yeterli değildir.
                </p>

                <h3 className={styles.seoSubtitle}>Metal karkas sistemlerde kullanım</h3>
                <p className={styles.seoText}>
                  C60/C100 profil karkasta bariyerli bondex sünger iki yüzey arasına sıkıştırılabilir veya tek tarafta
                  bariyer yüzü dışa bakacak şekilde sabitlenir. Profil titreşimini kırmak için levha profille temas
                  etmemeli; ara mesafe conta veya sünger şeridi ile dengelenir. Ağır bariyer katmanı karkas yükünü
                  artırır; profil aralığı ve ankraj hesabı buna göre yapılmalıdır. Çift karkas duvarlarda her iki
                  boşlukta bariyerli levha kullanımı izolasyonu yükseltir.
                </p>

                <h3 className={styles.seoSubtitle}>Yapıştırıcı ve montaj önerileri</h3>
                <p className={styles.seoText}>
                  Neopren bazlı contact adhesive veya PU köpük yapıştırıcı yaygın kullanılır. Nemli beton yüzeyde
                  primer şarttır. Yapıştırıcı sürüldükten sonra levha hızlıca yerleştirilir; hava kabarcığı rulosu ile
                  bastırılır. Mekanik sabitleme (dübel) genelde alçıpan tarafında yapılır; bariyer yüzeyine dübel
                  delmek sürekliliği bozar. Montaj sonrası 24 saat yapıştırıcı kuruması beklenir; hemen alçıpan
                  vidalanabilir.
                </p>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>3</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    💡
                  </span>
                  <h3 className={styles.seoTitle}>Bariyerli Bondex Sünger seçerken nelere dikkat edilmeli?</h3>
                </header>
                <p className={styles.seoText}>
                  <strong>Bariyerli bondex sünger</strong> seçiminde hedef izolasyon seviyesi, mevcut yapı kalınlığı ve montaj yüzeyi belirleyicidir. İnce detay ve kapı uygulamaları için{' '}
                  <Link to="/bariyerli-bondex-sunger-23mm" rel="follow">
                    23mm
                  </Link>{' '}
                  tercih edilirken, dengeli çözümler için{' '}
                  <Link to="/bariyerli-bondex-sunger-33mm" rel="follow">
                    33mm
                  </Link>{' '}
                  ve{' '}
                  <Link to="/bariyerli-bondex-sunger-43mm" rel="follow">
                    43mm
                  </Link>{' '}
                  öne çıkar. En yüksek ses kesimi için{' '}
                  <Link to="/bariyerli-bondex-sunger-53mm" rel="follow">
                    53mm Bariyerli Bondex Sünger
                  </Link>{' '}
                  veya çift bariyerli alternatifler değerlendirilebilir. Uygulama ve seçim rehberi için{' '}
                  <Link to="/urunler" rel="follow">
                    ürünler
                  </Link>{' '}
                  ve{' '}
                  <Link to="/blog-bariyerli-bondex-sunger" rel="follow">
                    blog sayfamızı
                  </Link>{' '}
                  ziyaret edebilirsiniz.
                </p>
                <div className={styles.bariyerliVisualBreakout}>
                  <div
                    className={styles.bariyerliVisualRow}
                    role="group"
                    aria-label="Standart Bondex ve Bariyerli Bondex seçim görselleri"
                  >
                    <figure className={styles.bariyerliVisualCard}>
                      <div className={styles.bariyerliVisualImgWrap}>
                        <img
                          className={styles.bariyerliVisualImg}
                          src="/images/bondex-2.webp"
                          alt="Standart Bondex sünger duvar ve bölme uygulaması"
                          loading="lazy"
                          width={400}
                          height={300}
                        />
                      </div>
                      <figcaption className={styles.bariyerliVisualCap}>Standart Bondex — duvar / bölme</figcaption>
                    </figure>
                    <figure className={styles.bariyerliVisualCard}>
                      <div className={styles.bariyerliVisualImgWrap}>
                        <img
                          className={styles.bariyerliVisualImg}
                          src="/images/bondex-3.webp"
                          alt="Standart Bondex sünger kapı uygulaması"
                          loading="lazy"
                          width={400}
                          height={300}
                        />
                      </div>
                      <figcaption className={styles.bariyerliVisualCap}>Standart Bondex — kapı</figcaption>
                    </figure>
                    <figure className={styles.bariyerliVisualCard}>
                      <div className={styles.bariyerliVisualImgWrap}>
                        <img
                          className={styles.bariyerliVisualImg}
                          src="/images/bariyerli-4.webp"
                          alt="Bariyerli Bondex Sünger 43 mm makine dairesi ve duvar uygulaması"
                          loading="lazy"
                          width={400}
                          height={300}
                        />
                      </div>
                      <figcaption className={styles.bariyerliVisualCap}>Bariyerli Bondex 43 mm</figcaption>
                    </figure>
                    <figure className={styles.bariyerliVisualCard}>
                      <div className={styles.bariyerliVisualImgWrap}>
                        <img
                          className={styles.bariyerliVisualImg}
                          src="/images/bariyerli-6.webp"
                          alt="Bariyerli Bondex Sünger ürün ve uygulama görseli"
                          loading="lazy"
                          width={400}
                          height={300}
                        />
                      </div>
                      <figcaption className={styles.bariyerliVisualCap}>Bariyerli Bondex — ürün görünümü</figcaption>
                    </figure>
                  </div>
                </div>
              </section>
            </div>
          ) : product.slug === 'bariyerli-bondex-sunger-23mm' ? (
            <div className={styles.seoWrap}>
              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>1</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    🎯
                  </span>
                  <h3 className={styles.seoTitle}>Bariyerli Bondex Sünger 23mm nedir?</h3>
                </header>
                <p className={styles.seoText}>
                  <strong>Bariyerli Bondex Sünger 23mm</strong>; Bondex sünger ile bariyer katmanının birleştiği, 23 milimetre kalınlığında{' '}
                  <strong>ince kompozit bariyerli bondex süngerdir</strong>. Kompakt yapısıyla <strong>duvar yalıtım</strong> ve{' '}
                  <strong>kapı ses yalıtımı</strong> uygulamalarında tercih edilir. <strong>Bariyerli ses yalıtım süngeri</strong> olarak,{' '}
                  ince yapısı sayesinde yer tasarrufu sağlarken bariyer katmanı ile performans artışı sağlar. İçerikte{' '}
                  <strong>Bondex sünger</strong> alternatifleriyle birlikte kıyaslanabilir.
                </p>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>2</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    📍
                  </span>
                  <h3 className={styles.seoTitle}>Bariyerli Bondex Sünger 23mm kullanım alanları</h3>
                </header>
                <p className={styles.seoText}>
                  Bariyerli Bondex Sünger 23mm; ince detaylar ve yer tasarrufu gerektiren projelerde tercih edilir. Duvar yalıtım, kapı ses yalıtımı ve{' '}
                  sınırlı alan uygulamalarında ideal performans sunar.
                </p>
                <ul className={styles.seoList}>
                  <li>Duvar yalıtım uygulamalarında ince kompozit çözüm</li>
                  <li>Kapı ses yalıtımı uygulamalarında pratik bariyerli ses yalıtım süngeri</li>
                  <li>Stüdyo ve sınırlı alan uygulamalarında yer tasarrufu sağlayan çözüm</li>
                  <li>Bariyerli bondex sünger ihtiyacı olan alanlarda kompakt performans</li>
                </ul>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>3</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    ⚙️
                  </span>
                  <h3 className={styles.seoTitle}>Bariyerli Bondex Sünger 23mm özellikleri</h3>
                </header>
                <p className={styles.seoText}>
                  Bariyerli Bondex Sünger 23mm seçiminde <strong>kalınlık</strong>, <strong>uygulama yüzeyi</strong> ve{' '}
                  <strong>hedef izolasyon seviyesi</strong> kritik rol oynar. 23mm kalınlık, duvar yalıtım ve kapı ses yalıtımı projelerinde{' '}
                  ince kompozit çözüm sunar; daha yüksek performans gerektiren projelerde kalın modeller değerlendirilebilir.
                </p>
                <ul className={styles.seoList}>
                  <li>23mm ince kalınlık ile yer tasarrufu sağlar</li>
                  <li>Bariyer katmanı ile klasik Bondex süngere göre daha yüksek izolasyon performansı</li>
                  <li>
                    Daha yüksek izolasyon için{' '}
                    <Link to="/bariyerli-bondex-sunger-33mm" rel="follow">
                      33mm
                    </Link>
                    ,{' '}
                    <Link to="/bariyerli-bondex-sunger-43mm" rel="follow">
                      Bariyerli Bondex Sünger 43mm
                    </Link>
                    ,{' '}
                    <Link to="/bariyerli-bondex-sunger-53mm" rel="follow">
                      Bariyerli Bondex Sünger 53mm
                    </Link>{' '}
                    Bariyerli Bondex Sünger alternatifleri tercih edilebilir
                  </li>
                  <li>
                    Klasik uygulamalar için{' '}
                    <Link to="/bondex-sunger" rel="follow">
                      Bondex sünger
                    </Link>{' '}
                    kalınlıkları, farklı <strong>Bariyerli Bondex Sünger</strong> modelleri için{' '}
                    <Link to="/" rel="follow">
                      Bariyerli Bondex Sünger
                    </Link>{' '}
                    sayfasından seçim yapılabilir
                  </li>
                </ul>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>4</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    💰
                  </span>
                  <h3 className={styles.seoTitle}>Bariyerli Bondex Sünger 23mm fiyatları</h3>
                </header>
                <p className={styles.seoText}>
                  <strong>Bariyerli bondex sünger fiyatı 23mm</strong> için; metraj, plaka ölçüsü ve proje adedine göre değişir.
                  Bariyerli bondex 23mm fiyatları için en doğru teklif almak üzere uygulama alanınızı (metraj), hedef izolasyon seviyenizi
                  ve proje detaylarınızı paylaşmanız yeterlidir. <strong>Ses yalıtım fiyatları</strong> hakkında hızlı bilgi için WhatsApp üzerinden iletişime
                  geçebilirsiniz. Diğer kalınlıklar için <Link to="/" rel="follow">Bariyerli Bondex Sünger</Link> sayfasını inceleyin.
                </p>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>5</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    💡
                  </span>
                  <h3 className={styles.seoTitle}>Bariyerli Bondex Sünger 23mm seçerken nelere dikkat edilmeli?</h3>
                </header>
                <p className={styles.seoText}>
                  Bariyerli Bondex Sünger 23mm seçimi; uygulama alanı, hedef izolasyon seviyesi ve mevcut yükseklik kısıtlarına göre
                  yapılmalıdır. 23mm kalınlık ince detaylar ve yer tasarrufu gerektiren projeler için idealdir. Daha yüksek izolasyon hedeflerinde{' '}
                  <Link to="/bariyerli-bondex-sunger-33mm" rel="follow">
                    33mm
                  </Link>
                  ,{' '}
                  <Link to="/bariyerli-bondex-sunger-43mm" rel="follow">
                    Bariyerli Bondex Sünger 43mm
                  </Link>
                  ,{' '}
                  <Link to="/bariyerli-bondex-sunger-53mm" rel="follow">
                    Bariyerli Bondex Sünger 53mm
                  </Link>{' '}
                  Bariyerli Bondex Sünger çözümleri değerlendirilebilir. Klasik uygulamalar için{' '}
                  <Link to="/bondex-sunger" rel="follow">
                    Bondex sünger
                  </Link>{' '}
                  kalınlıkları arasından seçim yapılabilir.
                </p>
              </section>
            </div>
          ) : product.slug === 'bariyerli-bondex-sunger-33mm' ? (
            <div className={styles.seoWrap}>
              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>1</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    🎯
                  </span>
                  <h3 className={styles.seoTitle}>Bariyerli Bondex Sünger 33mm nedir?</h3>
                </header>
                <p className={styles.seoText}>
                  <strong>Bariyerli Bondex Sünger 33mm</strong>, Bondex sünger gövdesi ile ağır bariyer katmanının birleştiği{' '}
                  <strong>bariyerli bondex sünger</strong> çözümüdür. 33mm kalınlık; <strong>düşük frekanslı sesleri</strong> ve titreşimi azaltmaya yardımcı olur, duvar ve kapı ses yalıtımı projelerinde dengeli performans sunar.{' '}
                  Bariyerli kompozit yapısı sayesinde endüstriyel ve teknik alanlarda yüksek ses yalıtımı sağlar; stüdyo ve ofis uygulamalarında da kütle etkisiyle konforu artırır.
                </p>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>2</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    📍
                  </span>
                  <h3 className={styles.seoTitle}>Bariyerli Bondex Sünger 33mm kullanım alanları</h3>
                </header>
                <p className={styles.seoText}>
                  <strong>Bariyerli Bondex Sünger 33mm</strong>; performans/kalınlık dengesi gerektiren projelerde tercih edilir. <strong>Bariyerli bondex sünger</strong> arayanlar için yaygın senaryolar:
                </p>
                <ul className={styles.seoList}>
                  <li>Duvar yalıtımı: düşük frekanslı hava yolu sesini kütle + bariyerle azaltır</li>
                  <li>Kapı ses yalıtımı: ağır bariyer katmanı ile sızdırmazlık ve ses kesimi</li>
                  <li>Stüdyo, kayıt/yayın odaları: dış gürültüyü keserken içerideki sesi sınırlar</li>
                  <li>Makine dairesi, ekipman odaları: titreşim + hava yolu sesini birlikte düşürür</li>
                  <li>Endüstriyel/teknik alanlar: üretim hattı, kompresör, jeneratör yan hacimleri</li>
                </ul>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>3</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    ⚙️
                  </span>
                  <h3 className={styles.seoTitle}>Bariyerli Bondex Sünger 33mm özellikleri ve alternatifler</h3>
                </header>
                <p className={styles.seoText}>
                  33mm kalınlık, bariyer katmanı sayesinde klasik{' '}
                  <Link to="/bondex-sunger" rel="follow">
                    Bondex sünger
                  </Link>{' '}
                  performansını yükseltir ve düşük frekansta kütle etkisini artırır. Projeye göre şu alternatifleri karşılaştırın:
                </p>
                <ul className={styles.seoList}>
                  <li>
                    İnce ve kompakt:{' '}
                    <Link to="/bariyerli-bondex-sunger-23mm" rel="follow">
                      Bariyerli Bondex Sünger 23mm
                    </Link>{' '}
                    – kapı ve sınırlı alan uygulamaları
                  </li>
                  <li>
                    Dengeli seviye (bu ürün):{' '}
                    <Link to="/bariyerli-bondex-sunger-33mm" rel="follow">
                      Bariyerli Bondex Sünger 33mm
                    </Link>{' '}
                    – duvar yalıtımı, ofis/stüdyo
                  </li>
                  <li>
                    Yüksek izolasyon:{' '}
                    <Link to="/bariyerli-bondex-sunger-43mm" rel="follow">
                      Bariyerli Bondex Sünger 43mm
                    </Link>{' '}
                    – makine dairesi, jeneratör
                  </li>
                  <li>
                    Maksimum bariyer:{' '}
                    <Link to="/bariyerli-bondex-sunger-53mm" rel="follow">
                      Bariyerli Bondex Sünger 53mm
                    </Link>{' '}
                    – en yüksek hava yolu ses kesimi
                  </li>
                  <li>
                    Daha yüksek seviye için{' '}
                    <Link to="/cift-bariyerli-bondex-sunger" rel="follow">
                      Çift Bariyerli Bondex
                    </Link>{' '}
                    veya kalın{' '}
                    <Link to="/bondex-sunger-50mm" rel="follow">
                      Bondex Sünger 50mm
                    </Link>{' '}
                    ile katmanlı çözümler değerlendirilebilir.
                  </li>
                  <li>
                    Tüm modeller ve teknik içerik için{' '}
                    <Link to="/urunler" rel="follow">
                      ürünler
                    </Link>{' '}
                    sayfasını ve{' '}
                    <Link to="/blog-bariyerli-bondex-sunger" rel="follow">
                      Bariyerli Bondex blog
                    </Link>{' '}
                    yazısını inceleyin.
                  </li>
                </ul>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>4</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    💰
                  </span>
                  <h3 className={styles.seoTitle}>Bariyerli Bondex Sünger 33mm fiyatları</h3>
                </header>
                <p className={styles.seoText}>
                  <strong>Bariyerli bondex sünger fiyatı 33mm</strong>; metraj, plaka ölçüsü ve proje detayına göre değişir. 33mm model için hedef dB düşümü, uygulama yüzeyi ve montaj alanını paylaşmanız yeterli;{' '}
                  WhatsApp üzerinden hızlı fiyat ve teslimat bilgisi alabilirsiniz. Daha yüksek performans gerekiyorsa{' '}
                  <Link to="/bariyerli-bondex-sunger-43mm" rel="follow">
                    43mm
                  </Link>{' '}
                  ve{' '}
                  <Link to="/bariyerli-bondex-sunger-53mm" rel="follow">
                    53mm
                  </Link>{' '}
                  seçeneklerine, ince detay için{' '}
                  <Link to="/bariyerli-bondex-sunger-23mm" rel="follow">
                    23mm
                  </Link>{' '}
                  seçeneğine bakabilirsiniz.
                </p>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>5</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    💡
                  </span>
                  <h3 className={styles.seoTitle}>Bariyerli Bondex Sünger 33mm seçerken nelere dikkat edilmeli?</h3>
                </header>
                <p className={styles.seoText}>
                  33mm seçimi; hedef izolasyon seviyesi, mevcut duvar/kapı kalınlığı ve montaj yüzeyine göre yapılmalıdır. Daha yüksek bariyer ihtiyacında{' '}
                  <Link to="/bariyerli-bondex-sunger-43mm" rel="follow">
                    Bariyerli Bondex Sünger 43mm
                  </Link>{' '}
                  veya{' '}
                  <Link to="/bariyerli-bondex-sunger-53mm" rel="follow">
                    Bariyerli Bondex Sünger 53mm
                  </Link>{' '}
                  tercih edilir; maksimum seviye için{' '}
                  <Link to="/cift-bariyerli-bondex-sunger" rel="follow">
                    Çift Bariyerli Bondex
                  </Link>{' '}
                  değerlendirilebilir. İnce ve kompakt projelerde{' '}
                  <Link to="/bariyerli-bondex-sunger-23mm" rel="follow">
                    Bariyerli Bondex Sünger 23mm
                  </Link>{' '}
                  yeterli olabilir. Klasik uygulamalarda{' '}
                  <Link to="/bondex-sunger" rel="follow">
                    Bondex sünger
                  </Link>{' '}
                  kalınlıkları ile katmanlı sistem kurabilirsiniz.
                </p>
              </section>
            </div>
          ) : product.slug === 'bariyerli-bondex-sunger-43mm' ? (
            <div className={styles.seoWrap}>
              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>1</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    🎯
                  </span>
                  <h3 className={styles.seoTitle}>Bariyerli Bondex Sünger 43mm nedir?</h3>
                </header>
                <p className={styles.seoText}>
                  <strong>Bariyerli Bondex Sünger 43mm</strong>, Bondex sünger gövdesi ve ağır bariyer katmanının birleştiği <strong>bariyerli bondex sünger</strong> çözümüdür. Kalın yapısı, geniş yüzey alanı ve çok katmanlı kurgusu ile <strong>yankı</strong> ve <strong>ortam gürültüsünü</strong> azaltır; düşük frekanslı seslerde kütle + bariyer etkisi sağlar.
                </p>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>2</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    📍
                  </span>
                  <h3 className={styles.seoTitle}>Bariyerli Bondex Sünger 43mm kullanım alanları</h3>
                </header>
                <p className={styles.seoText}>
                  <strong>Bariyerli Bondex Sünger 43mm</strong>, yüksek izolasyon ihtiyacı olan projelerde tercih edilir. Yaygın senaryolar:
                </p>
                <ul className={styles.seoList}>
                  <li>Duvar yalıtımı: düşük frekanslı hava yolu sesini kütle + bariyerle düşürür</li>
                  <li>Kapı ses yalıtımı: ağır bariyer katmanı ile sızdırmazlık ve ses kesimi</li>
                  <li>Makine dairesi, jeneratör, kompresör odaları: titreşim + hava yolu sesini birlikte azaltır</li>
                  <li>Stüdyo / yayın odası: dış gürültüyü kesip içerideki sesi sınırlar</li>
                  <li>Endüstriyel/teknik alanlar: üretim hattı, HVAC, ekipman odaları</li>
                </ul>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>3</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    ⚙️
                  </span>
                  <h3 className={styles.seoTitle}>Bariyerli Bondex Sünger 43mm özellikleri ve alternatifler</h3>
                </header>
                <p className={styles.seoText}>
                  43mm bariyerli yapı, klasik{' '}
                  <Link to="/bondex-sunger" rel="follow">
                    Bondex Sünger
                  </Link>{' '}
                  performansını yükseltir; kütle ve bariyer kombinasyonu düşük frekansta daha fazla ses kesimi sağlar. Projeye göre şu alternatifleri karşılaştırın:
                </p>
                <ul className={styles.seoList}>
                  <li>
                    İnce/kompakt:{' '}
                    <Link to="/bariyerli-bondex-sunger-23mm" rel="follow">
                      Bariyerli Bondex Sünger 23mm
                    </Link>{' '}
                    – kapı ve sınırlı alanlar
                  </li>
                  <li>
                    Dengeli:{' '}
                    <Link to="/bariyerli-bondex-sunger-33mm" rel="follow">
                      Bariyerli Bondex Sünger 33mm
                    </Link>{' '}
                    – ofis/stüdyo duvar yalıtımı
                  </li>
                  <li>
                    Yüksek (bu ürün):{' '}
                    <Link to="/bariyerli-bondex-sunger-43mm" rel="follow">
                      Bariyerli Bondex Sünger 43mm
                    </Link>{' '}
                    – makine dairesi, teknik hacimler
                  </li>
                  <li>
                    Maksimum bariyer:{' '}
                    <Link to="/bariyerli-bondex-sunger-53mm" rel="follow">
                      Bariyerli Bondex Sünger 53mm
                    </Link>{' '}
                    – en yüksek hava yolu ses kesimi
                  </li>
                  <li>
                    Daha yüksek seviye için{' '}
                    <Link to="/cift-bariyerli-bondex-sunger" rel="follow">
                      Çift Bariyerli Bondex
                    </Link>{' '}
                    veya kalın{' '}
                    <Link to="/bondex-sunger-50mm" rel="follow">
                      Bondex Sünger 50mm
                    </Link>{' '}
                    ile katmanlı çözümler değerlendirilebilir.
                  </li>
                  <li>
                    Tüm modeller ve teknik içerik için{' '}
                    <Link to="/urunler" rel="follow">
                      ürünler
                    </Link>{' '}
                    ve{' '}
                    <Link to="/blog-bariyerli-bondex-sunger" rel="follow">
                      Bariyerli Bondex blog
                    </Link>{' '}
                    sayfalarını inceleyin.
                  </li>
                </ul>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>4</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    💰
                  </span>
                  <h3 className={styles.seoTitle}>Bariyerli Bondex Sünger 43mm fiyatları</h3>
                </header>
                <p className={styles.seoText}>
                  <strong>Bariyerli bondex sünger fiyatı 43mm</strong>; metraj, plaka ölçüsü ve hedef dB düşümüne göre değişir. Proje detayınızı (metraj, uygulama yüzeyi, hedef izolasyon) paylaşarak WhatsApp üzerinden hızlı teklif alabilirsiniz. Daha yüksek bariyer ihtiyacı için{' '}
                  <Link to="/bariyerli-bondex-sunger-53mm" rel="follow">
                    Bariyerli Bondex Sünger 53mm
                  </Link>{' '}
                  seçeneğini, dengeli çözümler için{' '}
                  <Link to="/bariyerli-bondex-sunger-33mm" rel="follow">
                    Bariyerli Bondex Sünger 33mm
                  </Link>{' '}
                  veya ince detay için{' '}
                  <Link to="/bariyerli-bondex-sunger-23mm" rel="follow">
                    Bariyerli Bondex Sünger 23mm
                  </Link>{' '}
                  değerlendirebilirsiniz.
                </p>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>5</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    💡
                  </span>
                  <h3 className={styles.seoTitle}>Bariyerli Bondex Sünger 43mm seçerken nelere dikkat edilmeli?</h3>
                </header>
                <p className={styles.seoText}>
                  43mm seçimi; hedef izolasyon seviyesi, mevcut duvar/kapı kalınlığı ve montaj yüzeyine göre yapılmalıdır. Daha yüksek bariyer ihtiyacında{' '}
                  <Link to="/bariyerli-bondex-sunger-53mm" rel="follow">
                    Bariyerli Bondex Sünger 53mm
                  </Link>{' '}
                  tercih edilir; dengeli çözüm için{' '}
                  <Link to="/bariyerli-bondex-sunger-33mm" rel="follow">
                    Bariyerli Bondex Sünger 33mm
                  </Link>{' '}
                  veya ince uygulamalar için{' '}
                  <Link to="/bariyerli-bondex-sunger-23mm" rel="follow">
                    Bariyerli Bondex Sünger 23mm
                  </Link>{' '}
                  değerlendirilebilir. Klasik uygulamalarda{' '}
                  <Link to="/bondex-sunger" rel="follow">
                    Bondex Sünger
                  </Link>{' '}
                  kalınlıkları ile katmanlı sistem kurabilirsiniz; daha yüksek dB hedeflerinde{' '}
                  <Link to="/cift-bariyerli-bondex-sunger" rel="follow">
                    Çift Bariyerli Bondex
                  </Link>{' '}
                  seçeneğini göz önünde bulundurun.
                </p>
              </section>
            </div>
          ) : product.slug === 'bariyerli-bondex-sunger-53mm' ? (
            <div className={styles.seoWrap}>
              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>1</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    🎯
                  </span>
                  <h3 className={styles.seoTitle}>Bariyerli Bondex Sünger 53mm nedir?</h3>
                </header>
                <p className={styles.seoText}>
                  <strong>Bariyerli Bondex Sünger 53mm</strong>, ekstra kalın bariyer katmanı ve yüksek yoğunluklu gövdesiyle <strong>maksimum akustik performans</strong> hedefleyen <strong>bariyerli bondex sünger</strong> çözümüdür. Yoğun ses kaynaklarının kontrolünde, profesyonel ve endüstriyel projelerde üst seviye sonuç verir; duvar ve kapı ses yalıtımında kütle + bariyer etkisini en yüksek seviyeye taşır.
                </p>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>2</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    📍
                  </span>
                  <h3 className={styles.seoTitle}>Bariyerli Bondex Sünger 53mm kullanım alanları</h3>
                </header>
                <p className={styles.seoText}>
                  <strong>Bariyerli Bondex Sünger 53mm</strong>; maksimum izolasyon gerektiren projelerde, yoğun ses ve titreşim kontrolü için tercih edilir. Tipik senaryolar:
                </p>
                <ul className={styles.seoList}>
                  <li>Duvar yalıtımı: yoğun gürültü ve düşük frekanslar için kütle + bariyer etkisi</li>
                  <li>Kapı ses yalıtımı: ağır kanat ve sızdırmazlık gerektiren projeler</li>
                  <li>Makine dairesi, jeneratör, kompresör odaları: yüksek SPL ve titreşimli kaynaklar</li>
                  <li>Stüdyo / yayın / prova odaları: dış gürültüyü kesip iç sesin dışarı çıkmasını sınırlar</li>
                  <li>Endüstriyel/teknik hacimler: üretim hattı, HVAC, ekipman odaları</li>
                </ul>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>3</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    ⚙️
                  </span>
                  <h3 className={styles.seoTitle}>Bariyerli Bondex Sünger 53mm özellikleri</h3>
                </header>
                <p className={styles.seoText}>
                  53mm kalınlık ve bariyer katmanı, klasik{' '}
                  <Link to="/bondex-sunger" rel="follow">
                    Bondex Sünger
                  </Link>{' '}
                  kurgusuna göre en yüksek kütle ve bariyer etkisini sağlar. Projeye göre şu alternatifleri değerlendirin:
                </p>
                <ul className={styles.seoList}>
                  <li>
                    İnce/kompakt:{' '}
                    <Link to="/bariyerli-bondex-sunger-23mm" rel="follow">
                      Bariyerli Bondex Sünger 23mm
                    </Link>{' '}
                    – sınırlı kalınlık ve kapı uygulamaları
                  </li>
                  <li>
                    Dengeli:{' '}
                    <Link to="/bariyerli-bondex-sunger-33mm" rel="follow">
                      Bariyerli Bondex Sünger 33mm
                    </Link>{' '}
                    – ofis/stüdyo duvar yalıtımı
                  </li>
                  <li>
                    Yüksek:{' '}
                    <Link to="/bariyerli-bondex-sunger-43mm" rel="follow">
                      Bariyerli Bondex Sünger 43mm
                    </Link>{' '}
                    – teknik ve makine hacimleri
                  </li>
                  <li>
                    Maksimum (bu ürün):{' '}
                    <Link to="/bariyerli-bondex-sunger-53mm" rel="follow">
                      Bariyerli Bondex Sünger 53mm
                    </Link>{' '}
                    – en yüksek hava yolu ses kesimi
                  </li>
                  <li>
                    Daha yüksek seviye için{' '}
                    <Link to="/cift-bariyerli-bondex-sunger" rel="follow">
                      Çift Bariyerli Bondex
                    </Link>{' '}
                    veya kalın{' '}
                    <Link to="/bondex-sunger-50mm" rel="follow">
                      Bondex Sünger 50mm
                    </Link>{' '}
                    ile katmanlı çözümler değerlendirilebilir.
                  </li>
                  <li>
                    Tüm modeller ve teknik içerik için{' '}
                    <Link to="/urunler" rel="follow">
                      ürünler
                    </Link>{' '}
                    ve{' '}
                    <Link to="/blog-bariyerli-bondex-sunger" rel="follow">
                      Bariyerli Bondex blog
                    </Link>{' '}
                    sayfalarını inceleyin.
                  </li>
                </ul>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>4</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    💰
                  </span>
                  <h3 className={styles.seoTitle}>Bariyerli Bondex Sünger 53mm fiyatları</h3>
                </header>
                <p className={styles.seoText}>
                  <strong>Bariyerli bondex sünger fiyatı 53mm</strong>; metraj, plaka ölçüsü ve hedef dB düşümüne göre değişir. Proje detayınızı (metraj, uygulama yüzeyi, hedef izolasyon) paylaşarak WhatsApp üzerinden hızlı teklif alabilirsiniz. Daha dengeli çözümler için{' '}
                  <Link to="/bariyerli-bondex-sunger-33mm" rel="follow">
                    Bariyerli Bondex Sünger 33mm
                  </Link>{' '}
                  veya{' '}
                  <Link to="/bariyerli-bondex-sunger-43mm" rel="follow">
                    Bariyerli Bondex Sünger 43mm
                  </Link>{' '}
                  seçeneklerine; ince alanlar için{' '}
                  <Link to="/bariyerli-bondex-sunger-23mm" rel="follow">
                    Bariyerli Bondex Sünger 23mm
                  </Link>{' '}
                  modeline bakabilirsiniz.
                </p>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>5</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    💡
                  </span>
                  <h3 className={styles.seoTitle}>Bariyerli Bondex Sünger 53mm seçerken nelere dikkat edilmeli?</h3>
                </header>
                <p className={styles.seoText}>
                  53mm seçimi; hedef izolasyon seviyesi, mevcut duvar/kapı kalınlığı ve montaj yüzeyine göre yapılmalıdır. En yüksek bariyer ihtiyacında{' '}
                  <Link to="/bariyerli-bondex-sunger-53mm" rel="follow">
                    Bariyerli Bondex Sünger 53mm
                  </Link>{' '}
                  tercih edilir; daha dengeli senaryolar için{' '}
                  <Link to="/bariyerli-bondex-sunger-43mm" rel="follow">
                    Bariyerli Bondex Sünger 43mm
                  </Link>{' '}
                  veya{' '}
                  <Link to="/bariyerli-bondex-sunger-33mm" rel="follow">
                    Bariyerli Bondex Sünger 33mm
                  </Link>{' '}
                  değerlendirilebilir. İnce ve kompakt projelerde{' '}
                  <Link to="/bariyerli-bondex-sunger-23mm" rel="follow">
                    Bariyerli Bondex Sünger 23mm
                  </Link>{' '}
                  yeterli olabilir. Katmanlı sistem için{' '}
                  <Link to="/bondex-sunger-50mm" rel="follow">
                    Bondex Sünger 50mm
                  </Link>{' '}
                  veya{' '}
                  <Link to="/cift-bariyerli-bondex-sunger" rel="follow">
                    Çift Bariyerli Bondex
                  </Link>{' '}
                  kombinasyonlarını dikkate alın.
                </p>
              </section>
            </div>
          ) : product.slug === 'cift-bariyerli-bondex-sunger' ? (
            <div className={styles.seoWrap}>
              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>1</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    🎯
                  </span>
                  <h3 className={styles.seoTitle}>Çift Bariyerli Bondex Sünger nedir?</h3>
                </header>
                <p className={styles.seoText}>
                  <strong>Çift Bariyerli Bondex Sünger</strong>; iki bariyer katmanıyla, projelerde daha yüksek izolasyon hedefleyen uygulamalar için tasarlanmış{' '}
                  <strong>premium kompozit yalıtım malzemesidir</strong>. <strong>Bondex bariyerli sünger</strong> olarak,{' '}
                  <strong>bariyerli bondex ses yalıtım süngeri</strong> performansından daha yüksek izolasyon sunar. Duvar yalıtım ve{' '}
                  <strong>kapı ses yalıtımı</strong> uygulamalarında premium seçenektir. Kalınlık seçenekleriyle (26mm, 36mm, 46mm, 56mm) farklı ihtiyaçlara uyarlanır.
                </p>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>2</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    📍
                  </span>
                  <h3 className={styles.seoTitle}>Çift Bariyerli Bondex Sünger kullanım alanları</h3>
                </header>
                <p className={styles.seoText}>
                  Çift Bariyerli Bondex Sünger; maksimum izolasyon gerektiren projelerde tercih edilir. Duvar yalıtım, kapı ses yalıtımı, stüdyo ve{' '}
                  makine dairesi uygulamalarında premium performans sunar.
                </p>
                <ul className={styles.seoList}>
                  <li>Duvar yalıtım uygulamalarında maksimum seviye bariyerli bondex ses yalıtım süngeri çözümü</li>
                  <li>Kapı ses yalıtımı uygulamalarında premium izolasyon</li>
                  <li>Stüdyo ve makine dairesi uygulamalarında maksimum izolasyon</li>
                  <li>Bondex bariyerli sünger ihtiyacı olan alanlarda en güçlü performans</li>
                </ul>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>3</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    ⚙️
                  </span>
                  <h3 className={styles.seoTitle}>Çift Bariyerli Bondex Sünger özellikleri</h3>
                </header>
                <p className={styles.seoText}>
                  Çift Bariyerli Bondex Sünger seçiminde <strong>kalınlık</strong>, <strong>uygulama yüzeyi</strong> ve{' '}
                  <strong>hedef izolasyon seviyesi</strong> kritik rol oynar. İki bariyer katmanı, duvar yalıtım ve kapı ses yalıtımı projelerinde{' '}
                  maksimum performans sunar; premium seçenek olarak en yüksek izolasyon hedeflerinde tercih edilir.
                </p>
                <ul className={styles.seoList}>
                  <li>İki bariyer katmanı ile maksimum izolasyon performansı sağlar</li>
                  <li>Bondex bariyerli sünger olarak, klasik Bariyerli Bondex Sünger\'e göre daha yüksek performans</li>
                  <li>
                    Kalınlık seçenekleri:{' '}
                    <Link to="/cift-bariyerli-bondex-sunger-26mm" rel="follow">
                      26mm
                    </Link>
                    ,{' '}
                    <Link to="/cift-bariyerli-bondex-sunger-36mm" rel="follow">
                      36mm
                    </Link>
                    ,{' '}
                    <Link to="/cift-bariyerli-bondex-sunger-46mm" rel="follow">
                      46mm
                    </Link>
                    ,{' '}
                    <Link to="/cift-bariyerli-bondex-sunger-56mm" rel="follow">
                      56mm
                    </Link>
                  </li>
                  <li>
                    Yüksek izolasyon için{' '}
                    <Link to="/cift-bariyerli-bondex-sunger" rel="follow">
                      Çift Bariyerli Bondex Sünger
                    </Link>
                    , klasik uygulamalar için{' '}
                    <Link to="/bondex-sunger" rel="follow">
                      Bondex sünger
                    </Link>{' '}
                    kalınlıkları arasından seçim yapılabilir
                  </li>
                </ul>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>4</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    💰
                  </span>
                  <h3 className={styles.seoTitle}>Çift Bariyerli Bondex Sünger fiyatları</h3>
                </header>
                <p className={styles.seoText}>
                  <strong>Bondex bariyerli sünger fiyatı</strong>; kalınlık, metraj, plaka ölçüsü ve proje adedine göre değişir.
                  Çift Bariyerli Bondex Sünger fiyatları için en doğru teklif almak üzere uygulama alanınızı (metraj), hedef izolasyon seviyenizi
                  ve proje detaylarınızı paylaşmanız yeterlidir. <strong>Ses yalıtım fiyatları</strong> hakkında hızlı bilgi için WhatsApp üzerinden iletişime
                  geçebilirsiniz. Farklı kalınlıklar için <Link to="/cift-bariyerli-bondex-sunger" rel="follow">Çift Bariyerli Bondex Sünger</Link> modellerini inceleyin.
                </p>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>5</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    💡
                  </span>
                  <h3 className={styles.seoTitle}>Çift Bariyerli Bondex Sünger seçerken nelere dikkat edilmeli?</h3>
                </header>
                <p className={styles.seoText}>
                  Çift Bariyerli Bondex Sünger seçimi; uygulama alanı, hedef izolasyon seviyesi ve mevcut yükseklik kısıtlarına göre
                  yapılmalıdır. İki bariyer katmanı maksimum izolasyon gerektiren projeler için idealdir. Premium seçenek olarak en yüksek{' '}
                  izolasyon hedeflerinde tercih edilir. Yüksek izolasyon için{' '}
                  <Link to="/" rel="follow">
                    Bariyerli Bondex Sünger
                  </Link>
                  , klasik uygulamalar için{' '}
                  <Link to="/bondex-sunger" rel="follow">
                    Bondex sünger
                  </Link>{' '}
                  kalınlıkları arasından seçim yapılabilir.
                </p>
              </section>
            </div>
          ) : product.slug === 'cift-bariyerli-bondex-sunger-26mm' ? (
            <div className={styles.seoWrap}>
              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>1</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    🎯
                  </span>
                  <h3 className={styles.seoTitle}>Çift Bariyerli Bondex Sünger 26mm nedir?</h3>
                </header>
                <p className={styles.seoText}>
                  <strong>Çift Bariyerli Bondex Sünger 26mm</strong>; iki bariyer katmanıyla, 26 milimetre kalınlığında{' '}
                  <strong>kompakt premium kompozit yalıtım malzemesidir</strong>. <strong>Bondex bariyerli sünger</strong> olarak,{' '}
                  sınırlı alanda yüksek izolasyon hedefleyen projeler için ideal bir seçenektir. <strong>Bariyerli bondex ses yalıtım süngeri</strong> performansından{' '}
                  daha yüksek izolasyon sunar. Duvar yalıtım ve <strong>kapı ses yalıtımı</strong> uygulamalarında kompakt premium çözümdür.
                </p>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>2</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    📍
                  </span>
                  <h3 className={styles.seoTitle}>Çift Bariyerli Bondex Sünger 26mm kullanım alanları</h3>
                </header>
                <p className={styles.seoText}>
                  Çift Bariyerli Bondex Sünger 26mm; kompakt premium izolasyon gerektiren projelerde tercih edilir. Duvar yalıtım, kapı ses yalıtımı, stüdyo ve{' '}
                  sınırlı alan uygulamalarında ideal performans sunar.
                </p>
                <ul className={styles.seoList}>
                  <li>Duvar yalıtım uygulamalarında kompakt premium bariyerli bondex ses yalıtım süngeri çözümü</li>
                  <li>Kapı ses yalıtımı uygulamalarında pratik premium izolasyon</li>
                  <li>Stüdyo ve sınırlı alan uygulamalarında yer tasarrufu sağlayan çözüm</li>
                  <li>Bondex bariyerli sünger ihtiyacı olan alanlarda kompakt performans</li>
                </ul>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>3</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    ⚙️
                  </span>
                  <h3 className={styles.seoTitle}>Çift Bariyerli Bondex Sünger 26mm özellikleri</h3>
                </header>
                <p className={styles.seoText}>
                  Çift Bariyerli Bondex Sünger 26mm seçiminde <strong>kalınlık</strong>, <strong>uygulama yüzeyi</strong> ve{' '}
                  <strong>hedef izolasyon seviyesi</strong> kritik rol oynar. 26mm kalınlık, duvar yalıtım ve kapı ses yalıtımı projelerinde{' '}
                  kompakt premium çözüm sunar; daha yüksek performans gerektiren projelerde kalın modeller değerlendirilebilir.
                </p>
                <ul className={styles.seoList}>
                  <li>26mm kompakt kalınlık ile yer tasarrufu sağlar</li>
                  <li>İki bariyer katmanı ile klasik Bariyerli Bondex Sünger\'e göre daha yüksek izolasyon performansı</li>
                  <li>
                    Daha yüksek izolasyon için{' '}
                    <Link to="/cift-bariyerli-bondex-sunger-36mm" rel="follow">
                      36mm
                    </Link>
                    ,{' '}
                    <Link to="/cift-bariyerli-bondex-sunger-46mm" rel="follow">
                      46mm
                    </Link>
                    ,{' '}
                    <Link to="/cift-bariyerli-bondex-sunger-56mm" rel="follow">
                      56mm
                    </Link>{' '}
                    Çift Bariyerli Bondex Sünger alternatifleri tercih edilebilir
                  </li>
                  <li>
                    Yüksek izolasyon için{' '}
                    <Link to="/cift-bariyerli-bondex-sunger" rel="follow">
                      Çift Bariyerli Bondex Sünger
                    </Link>
                    , klasik uygulamalar için{' '}
                    <Link to="/bondex-sunger" rel="follow">
                      Bondex sünger
                    </Link>
                    , farklı <strong>Çift Bariyerli Bondex Sünger</strong> modelleri için{' '}
                    <Link to="/cift-bariyerli-bondex-sunger" rel="follow">
                      Çift Bariyerli Bondex Sünger
                    </Link>{' '}
                    sayfasından seçim yapılabilir
                  </li>
                </ul>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>4</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    💰
                  </span>
                  <h3 className={styles.seoTitle}>Çift Bariyerli Bondex Sünger 26mm fiyatları</h3>
                </header>
                <p className={styles.seoText}>
                  <strong>Bondex bariyerli sünger fiyatı 26mm</strong> için; metraj, plaka ölçüsü ve proje adedine göre değişir.
                  Çift Bariyerli Bondex Sünger 26mm fiyatları için en doğru teklif almak üzere uygulama alanınızı (metraj), hedef izolasyon seviyenizi
                  ve proje detaylarınızı paylaşmanız yeterlidir. <strong>Ses yalıtım fiyatları</strong> hakkında hızlı bilgi için WhatsApp üzerinden iletişime
                  geçebilirsiniz. Diğer kalınlıklar için <Link to="/cift-bariyerli-bondex-sunger" rel="follow">Çift Bariyerli Bondex Sünger</Link> sayfasını inceleyin.
                </p>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>5</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    💡
                  </span>
                  <h3 className={styles.seoTitle}>Çift Bariyerli Bondex Sünger 26mm seçerken nelere dikkat edilmeli?</h3>
                </header>
                <p className={styles.seoText}>
                  Çift Bariyerli Bondex Sünger 26mm seçimi; uygulama alanı, hedef izolasyon seviyesi ve mevcut yükseklik kısıtlarına göre
                  yapılmalıdır. 26mm kalınlık kompakt premium izolasyon gerektiren projeler için idealdir. Daha yüksek izolasyon hedeflerinde{' '}
                  <Link to="/cift-bariyerli-bondex-sunger-36mm" rel="follow">
                    36mm
                  </Link>
                  ,{' '}
                  <Link to="/cift-bariyerli-bondex-sunger-46mm" rel="follow">
                    46mm
                  </Link>
                  ,{' '}
                  <Link to="/cift-bariyerli-bondex-sunger-56mm" rel="follow">
                    56mm
                  </Link>{' '}
                  Çift Bariyerli Bondex Sünger çözümleri değerlendirilebilir. Yüksek izolasyon için{' '}
                  <Link to="/cift-bariyerli-bondex-sunger" rel="follow">
                    Çift Bariyerli Bondex Sünger
                  </Link>
                  , klasik uygulamalar için{' '}
                  <Link to="/bondex-sunger" rel="follow">
                    Bondex sünger
                  </Link>{' '}
                  kalınlıkları arasından seçim yapılabilir.
                </p>
              </section>
            </div>
          ) : product.slug === 'cift-bariyerli-bondex-sunger-36mm' ? (
            <div className={styles.seoWrap}>
              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>1</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    🎯
                  </span>
                  <h3 className={styles.seoTitle}>Çift Bariyerli Bondex Sünger 36mm nedir?</h3>
                </header>
                <p className={styles.seoText}>
                  <strong>Çift Bariyerli Bondex Sünger 36mm</strong>; iki bariyer katmanıyla, 36 milimetre kalınlığında{' '}
                  <strong>dengeli premium kompozit yalıtım malzemesidir</strong>. <strong>Bondex bariyerli sünger</strong> olarak,{' '}
                  bariyer performansını artırmak isteyen uygulamalar için ideal bir seçenektir. <strong>Bariyerli bondex ses yalıtım süngeri</strong> performansından{' '}
                  daha yüksek izolasyon sunar. Duvar yalıtım ve <strong>kapı ses yalıtımı</strong> uygulamalarında dengeli premium çözümdür.
                </p>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>2</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    📍
                  </span>
                  <h3 className={styles.seoTitle}>Çift Bariyerli Bondex Sünger 36mm kullanım alanları</h3>
                </header>
                <p className={styles.seoText}>
                  Çift Bariyerli Bondex Sünger 36mm; dengeli premium izolasyon gerektiren projelerde tercih edilir. Duvar yalıtım, kapı ses yalıtımı, stüdyo ve{' '}
                  makine dairesi uygulamalarında ideal performans sunar.
                </p>
                <ul className={styles.seoList}>
                  <li>Duvar yalıtım uygulamalarında dengeli premium bariyerli bondex ses yalıtım süngeri çözümü</li>
                  <li>Kapı ses yalıtımı uygulamalarında performans/kalınlık dengesi</li>
                  <li>Stüdyo ve makine dairesi uygulamalarında güçlü izolasyon</li>
                  <li>Bondex bariyerli sünger ihtiyacı olan alanlarda optimal performans</li>
                </ul>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>3</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    ⚙️
                  </span>
                  <h3 className={styles.seoTitle}>Çift Bariyerli Bondex Sünger 36mm özellikleri</h3>
                </header>
                <p className={styles.seoText}>
                  Çift Bariyerli Bondex Sünger 36mm seçiminde <strong>kalınlık</strong>, <strong>uygulama yüzeyi</strong> ve{' '}
                  <strong>hedef izolasyon seviyesi</strong> kritik rol oynar. 36mm kalınlık, duvar yalıtım ve kapı ses yalıtımı projelerinde{' '}
                  dengeli premium çözüm sunar; daha yüksek performans gerektiren projelerde kalın modeller değerlendirilebilir.
                </p>
                <ul className={styles.seoList}>
                  <li>36mm dengeli kalınlık ile performans ve yer tasarrufu sağlar</li>
                  <li>İki bariyer katmanı ile klasik Bariyerli Bondex Sünger\'e göre daha yüksek izolasyon performansı</li>
                  <li>
                    Daha yüksek izolasyon için{' '}
                    <Link to="/cift-bariyerli-bondex-sunger-46mm" rel="follow">
                      46mm
                    </Link>
                    ,{' '}
                    <Link to="/cift-bariyerli-bondex-sunger-56mm" rel="follow">
                      56mm
                    </Link>{' '}
                    Çift Bariyerli Bondex Sünger alternatifleri tercih edilebilir
                  </li>
                  <li>
                    Kompakt seçim için{' '}
                    <Link to="/cift-bariyerli-bondex-sunger-26mm" rel="follow">
                      26mm
                    </Link>
                    , yüksek izolasyon için{' '}
                    <Link to="/" rel="follow">
                      Bariyerli Bondex Sünger
                    </Link>
                    , klasik uygulamalar için{' '}
                    <Link to="/bondex-sunger" rel="follow">
                      Bondex sünger
                    </Link>
                    , farklı <strong>Çift Bariyerli Bondex Sünger</strong> modelleri için{' '}
                    <Link to="/cift-bariyerli-bondex-sunger" rel="follow">
                      Çift Bariyerli Bondex Sünger
                    </Link>{' '}
                    sayfasından seçim yapılabilir
                  </li>
                </ul>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>4</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    💰
                  </span>
                  <h3 className={styles.seoTitle}>Çift Bariyerli Bondex Sünger 36mm fiyatları</h3>
                </header>
                <p className={styles.seoText}>
                  <strong>Bondex bariyerli sünger fiyatı 36mm</strong> için; metraj, plaka ölçüsü ve proje adedine göre değişir.
                  Çift Bariyerli Bondex Sünger 36mm fiyatları için en doğru teklif almak üzere uygulama alanınızı (metraj), hedef izolasyon seviyenizi
                  ve proje detaylarınızı paylaşmanız yeterlidir. <strong>Ses yalıtım fiyatları</strong> hakkında hızlı bilgi için WhatsApp üzerinden iletişime
                  geçebilirsiniz. Diğer kalınlıklar için <Link to="/cift-bariyerli-bondex-sunger" rel="follow">Çift Bariyerli Bondex Sünger</Link> sayfasını inceleyin.
                </p>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>5</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    💡
                  </span>
                  <h3 className={styles.seoTitle}>Çift Bariyerli Bondex Sünger 36mm seçerken nelere dikkat edilmeli?</h3>
                </header>
                <p className={styles.seoText}>
                  Çift Bariyerli Bondex Sünger 36mm seçimi; uygulama alanı, hedef izolasyon seviyesi ve mevcut yükseklik kısıtlarına göre
                  yapılmalıdır. 36mm kalınlık dengeli premium izolasyon gerektiren projeler için idealdir. Daha yüksek izolasyon hedeflerinde{' '}
                  <Link to="/cift-bariyerli-bondex-sunger-46mm" rel="follow">
                    46mm
                  </Link>
                  ,{' '}
                  <Link to="/cift-bariyerli-bondex-sunger-56mm" rel="follow">
                    56mm
                  </Link>{' '}
                  Çift Bariyerli Bondex Sünger çözümleri değerlendirilebilir. Kompakt seçim için{' '}
                  <Link to="/cift-bariyerli-bondex-sunger-26mm" rel="follow">
                    26mm
                  </Link>
                  , yüksek izolasyon için{' '}
                  <Link to="/" rel="follow">
                    Bariyerli Bondex Sünger
                  </Link>
                  , klasik uygulamalar için{' '}
                  <Link to="/bondex-sunger" rel="follow">
                    Bondex sünger
                  </Link>{' '}
                  kalınlıkları arasından seçim yapılabilir.
                </p>
              </section>
            </div>
          ) : product.slug === 'cift-bariyerli-bondex-sunger-46mm' ? (
            <div className={styles.seoWrap}>
              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>1</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    🎯
                  </span>
                  <h3 className={styles.seoTitle}>Çift Bariyerli Bondex Sünger 46mm nedir?</h3>
                </header>
                <p className={styles.seoText}>
                  <strong>Çift Bariyerli Bondex Sünger 46mm</strong>; iki bariyer katmanıyla, 46 milimetre kalınlığında{' '}
                  <strong>yüksek performanslı premium kompozit yalıtım malzemesidir</strong>. <strong>Bondex bariyerli sünger</strong> olarak,{' '}
                  daha yüksek izolasyon hedefi olan projelerde ideal bir seçenektir. <strong>Bariyerli bondex ses yalıtım süngeri</strong> performansından{' '}
                  daha yüksek izolasyon sunar. Duvar yalıtım ve <strong>kapı ses yalıtımı</strong> uygulamalarında yüksek performanslı premium çözümdür.
                </p>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>2</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    📍
                  </span>
                  <h3 className={styles.seoTitle}>Çift Bariyerli Bondex Sünger 46mm kullanım alanları</h3>
                </header>
                <p className={styles.seoText}>
                  Çift Bariyerli Bondex Sünger 46mm; yüksek performanslı premium izolasyon gerektiren projelerde tercih edilir. Duvar yalıtım, kapı ses yalıtımı, stüdyo ve{' '}
                  makine dairesi uygulamalarında yüksek performans sunar.
                </p>
                <ul className={styles.seoList}>
                  <li>Duvar yalıtım uygulamalarında yüksek seviye bariyerli bondex ses yalıtım süngeri çözümü</li>
                  <li>Kapı ses yalıtımı uygulamalarında premium izolasyon</li>
                  <li>Stüdyo ve makine dairesi uygulamalarında maksimum izolasyon</li>
                  <li>Bondex bariyerli sünger ihtiyacı olan alanlarda güçlü performans</li>
                </ul>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>3</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    ⚙️
                  </span>
                  <h3 className={styles.seoTitle}>Çift Bariyerli Bondex Sünger 46mm özellikleri</h3>
                </header>
                <p className={styles.seoText}>
                  Çift Bariyerli Bondex Sünger 46mm seçiminde <strong>kalınlık</strong>, <strong>uygulama yüzeyi</strong> ve{' '}
                  <strong>hedef izolasyon seviyesi</strong> kritik rol oynar. 46mm kalınlık, duvar yalıtım ve kapı ses yalıtımı projelerinde{' '}
                  yüksek performanslı premium çözüm sunar; maksimum performans gerektiren projelerde 56mm değerlendirilebilir.
                </p>
                <ul className={styles.seoList}>
                  <li>46mm yüksek kalınlık ile premium izolasyon performansı sağlar</li>
                  <li>İki bariyer katmanı ile klasik Bariyerli Bondex Sünger\'e göre çok daha yüksek izolasyon performansı</li>
                  <li>
                    Maksimum izolasyon için{' '}
                    <Link to="/cift-bariyerli-bondex-sunger-56mm" rel="follow">
                      56mm
                    </Link>{' '}
                    Çift Bariyerli Bondex Sünger alternatifi tercih edilebilir
                  </li>
                  <li>
                    Dengeli seçim için{' '}
                    <Link to="/cift-bariyerli-bondex-sunger-36mm" rel="follow">
                      36mm
                    </Link>
                    , kompakt seçim için{' '}
                    <Link to="/cift-bariyerli-bondex-sunger-26mm" rel="follow">
                      26mm
                    </Link>
                    , yüksek izolasyon için{' '}
                    <Link to="/" rel="follow">
                      Bariyerli Bondex Sünger
                    </Link>
                    , klasik uygulamalar için{' '}
                    <Link to="/bondex-sunger" rel="follow">
                      Bondex sünger
                    </Link>
                    , farklı <strong>Çift Bariyerli Bondex Sünger</strong> modelleri için{' '}
                    <Link to="/cift-bariyerli-bondex-sunger" rel="follow">
                      Çift Bariyerli Bondex Sünger
                    </Link>{' '}
                    sayfasından seçim yapılabilir
                  </li>
                </ul>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>4</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    💰
                  </span>
                  <h3 className={styles.seoTitle}>Çift Bariyerli Bondex Sünger 46mm fiyatları</h3>
                </header>
                <p className={styles.seoText}>
                  <strong>Bondex bariyerli sünger fiyatı 46mm</strong> için; metraj, plaka ölçüsü ve proje adedine göre değişir.
                  Çift Bariyerli Bondex Sünger 46mm fiyatları için en doğru teklif almak üzere uygulama alanınızı (metraj), hedef izolasyon seviyenizi
                  ve proje detaylarınızı paylaşmanız yeterlidir. <strong>Ses yalıtım fiyatları</strong> hakkında hızlı bilgi için WhatsApp üzerinden iletişime
                  geçebilirsiniz. Diğer kalınlıklar için <Link to="/cift-bariyerli-bondex-sunger" rel="follow">Çift Bariyerli Bondex Sünger</Link> sayfasını inceleyin.
                </p>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>5</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    💡
                  </span>
                  <h3 className={styles.seoTitle}>Çift Bariyerli Bondex Sünger 46mm seçerken nelere dikkat edilmeli?</h3>
                </header>
                <p className={styles.seoText}>
                  Çift Bariyerli Bondex Sünger 46mm seçimi; uygulama alanı, hedef izolasyon seviyesi ve mevcut yükseklik kısıtlarına göre
                  yapılmalıdır. 46mm kalınlık yüksek performanslı premium izolasyon gerektiren projeler için idealdir. Maksimum izolasyon hedeflerinde{' '}
                  <Link to="/cift-bariyerli-bondex-sunger-56mm" rel="follow">
                    56mm
                  </Link>{' '}
                  Çift Bariyerli Bondex Sünger çözümü değerlendirilebilir. Dengeli seçim için{' '}
                  <Link to="/cift-bariyerli-bondex-sunger-36mm" rel="follow">
                    36mm
                  </Link>
                  , yüksek izolasyon için{' '}
                  <Link to="/" rel="follow">
                    Bariyerli Bondex Sünger
                  </Link>
                  , klasik uygulamalar için{' '}
                  <Link to="/bondex-sunger" rel="follow">
                    Bondex sünger
                  </Link>{' '}
                  kalınlıkları arasından seçim yapılabilir.
                </p>
              </section>
            </div>
          ) : product.slug === 'cift-bariyerli-bondex-sunger-56mm' ? (
            <div className={styles.seoWrap}>
              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>1</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    🎯
                  </span>
                  <h3 className={styles.seoTitle}>Çift Bariyerli Bondex Sünger 56mm nedir?</h3>
                </header>
                <p className={styles.seoText}>
                  <strong>Çift Bariyerli Bondex Sünger 56mm</strong>; iki bariyer katmanıyla, 56 milimetre kalınlığında{' '}
                  <strong>maksimum performanslı premium kompozit yalıtım malzemesidir</strong>. <strong>Bondex bariyerli sünger</strong> olarak,{' '}
                  premium maksimum izolasyon hedefleyen uygulamalarda ideal bir seçenektir. <strong>Bariyerli bondex ses yalıtım süngeri</strong> performansından{' '}
                  en yüksek izolasyon sunar. Duvar yalıtım ve <strong>kapı ses yalıtımı</strong> uygulamalarında maksimum premium çözümdür.
                </p>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>2</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    📍
                  </span>
                  <h3 className={styles.seoTitle}>Çift Bariyerli Bondex Sünger 56mm kullanım alanları</h3>
                </header>
                <p className={styles.seoText}>
                  Çift Bariyerli Bondex Sünger 56mm; maksimum premium izolasyon gerektiren projelerde tercih edilir. Duvar yalıtım, kapı ses yalıtımı, stüdyo ve{' '}
                  makine dairesi uygulamalarında maksimum performans sunar.
                </p>
                <ul className={styles.seoList}>
                  <li>Duvar yalıtım uygulamalarında maksimum seviye bariyerli bondex ses yalıtım süngeri çözümü</li>
                  <li>Kapı ses yalıtımı uygulamalarında premium izolasyon</li>
                  <li>Stüdyo ve makine dairesi uygulamalarında maksimum izolasyon</li>
                  <li>Bondex bariyerli sünger ihtiyacı olan alanlarda en güçlü performans</li>
                </ul>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>3</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    ⚙️
                  </span>
                  <h3 className={styles.seoTitle}>Çift Bariyerli Bondex Sünger 56mm özellikleri</h3>
                </header>
                <p className={styles.seoText}>
                  Çift Bariyerli Bondex Sünger 56mm seçiminde <strong>kalınlık</strong>, <strong>uygulama yüzeyi</strong> ve{' '}
                  <strong>hedef izolasyon seviyesi</strong> kritik rol oynar. 56mm kalınlık, duvar yalıtım ve kapı ses yalıtımı projelerinde{' '}
                  maksimum premium çözüm sunar; premium seçenek olarak en yüksek izolasyon hedeflerinde tercih edilir.
                </p>
                <ul className={styles.seoList}>
                  <li>56mm maksimum kalınlık ile premium izolasyon performansı sağlar</li>
                  <li>İki bariyer katmanı ile klasik Bariyerli Bondex Sünger\'e göre en yüksek izolasyon performansı</li>
                  <li>
                    Aynı projede{' '}
                    <Link to="/bondex-sunger" rel="follow">
                      Bondex sünger
                    </Link>{' '}
                    ile katmanlı çözümler kurulabilir
                  </li>
                  <li>
                    Yüksek seviye için{' '}
                    <Link to="/cift-bariyerli-bondex-sunger-46mm" rel="follow">
                      46mm
                    </Link>
                    , dengeli seçim için{' '}
                    <Link to="/cift-bariyerli-bondex-sunger-36mm" rel="follow">
                      36mm
                    </Link>
                    , kompakt seçim için{' '}
                    <Link to="/cift-bariyerli-bondex-sunger-26mm" rel="follow">
                      26mm
                    </Link>
                    , yüksek izolasyon için{' '}
                    <Link to="/" rel="follow">
                      Bariyerli Bondex Sünger
                    </Link>
                    , klasik uygulamalar için{' '}
                    <Link to="/bondex-sunger" rel="follow">
                      Bondex sünger
                    </Link>
                    , farklı <strong>Çift Bariyerli Bondex Sünger</strong> modelleri için{' '}
                    <Link to="/cift-bariyerli-bondex-sunger" rel="follow">
                      Çift Bariyerli Bondex Sünger
                    </Link>{' '}
                    sayfasından seçim yapılabilir
                  </li>
                </ul>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>4</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    💰
                  </span>
                  <h3 className={styles.seoTitle}>Çift Bariyerli Bondex Sünger 56mm fiyatları</h3>
                </header>
                <p className={styles.seoText}>
                  <strong>Bondex bariyerli sünger fiyatı 56mm</strong> için; metraj, plaka ölçüsü ve proje adedine göre değişir.
                  Çift Bariyerli Bondex Sünger 56mm fiyatları için en doğru teklif almak üzere uygulama alanınızı (metraj), hedef izolasyon seviyenizi
                  ve proje detaylarınızı paylaşmanız yeterlidir. <strong>Ses yalıtım fiyatları</strong> hakkında hızlı bilgi için WhatsApp üzerinden iletişime
                  geçebilirsiniz. Diğer kalınlıklar için <Link to="/cift-bariyerli-bondex-sunger" rel="follow">Çift Bariyerli Bondex Sünger</Link> sayfasını inceleyin.
                </p>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>5</span>
                  <span className={styles.seoEmoji} aria-hidden="true">
                    💡
                  </span>
                  <h3 className={styles.seoTitle}>Çift Bariyerli Bondex Sünger 56mm seçerken nelere dikkat edilmeli?</h3>
                </header>
                <p className={styles.seoText}>
                  Çift Bariyerli Bondex Sünger 56mm seçimi; uygulama alanı, hedef izolasyon seviyesi ve mevcut yükseklik kısıtlarına göre
                  yapılmalıdır. 56mm kalınlık maksimum premium izolasyon gerektiren projeler için idealdir. Premium seçenek olarak en yüksek{' '}
                  izolasyon hedeflerinde tercih edilir. Yüksek seviye için{' '}
                  <Link to="/cift-bariyerli-bondex-sunger-46mm" rel="follow">
                    46mm
                  </Link>
                  , yüksek izolasyon için{' '}
                  <Link to="/" rel="follow">
                    Bariyerli Bondex Sünger
                  </Link>
                  , klasik uygulamalar için{' '}
                  <Link to="/bondex-sunger" rel="follow">
                    Bondex sünger
                  </Link>{' '}
                  kalınlıkları arasından seçim yapılabilir.
                </p>
              </section>
            </div>
          ) : (
            <div className={styles.seoWrap}>
              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>1</span>
                  <span className={styles.seoEmoji} aria-hidden="true">🎯</span>
                  <h3 className={styles.seoTitle}>{product.name} nedir?</h3>
                </header>
                <p className={styles.seoText}>
                  <strong>{product.name}</strong> {product.description} {product.category === 'Bondex Sünger'
                    ? 'Bondex sünger'
                    : product.category === 'Bariyerli Bondex'
                      ? 'Bariyerli Bondex Sünger'
                      : 'Çift Bariyerli Bondex Sünger'} ailesinin{' '}
                  {product.thicknessMm ? `${product.thicknessMm}mm kalınlığında` : 'temel'} bir üyesidir.
                  {product.category === 'Bondex Sünger'
                    ? ' Yüksek yoğunluklu rebonded süngerpan teknolojisiyle üretilen bu ürün, ses yalıtımı ve titreşim sönümleme uygulamalarında güvenilir bir çözümdür.'
                    : product.category === 'Bariyerli Bondex'
                      ? ' Bondex sünger ile bariyer katmanının birleştiği bu kompozit yapı, daha yüksek izolasyon performansı sağlar.'
                      : ' İki bariyer katmanıyla premium maksimum izolasyon hedefleyen bu ürün, en yüksek performans gerektiren projeler için idealdir.'}
                </p>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>2</span>
                  <span className={styles.seoEmoji} aria-hidden="true">📍</span>
                  <h3 className={styles.seoTitle}>{product.name} kullanım alanları</h3>
                </header>
                <p className={styles.seoText}>
                  <strong>{product.name}</strong> farklı mekanlarda pratik uygulanabilirliğiyle öne çıkar.
                  {product.thicknessMm
                    ? ` ${product.thicknessMm}mm kalınlığı, `
                    : ' Esnek kalınlık seçenekleri ile '}
                  {product.thicknessMm && product.thicknessMm <= 20
                    ? 'ince detaylar ve sınırlı alanlarda yer tasarrufu sağlar.'
                    : product.thicknessMm && product.thicknessMm <= 30
                      ? 'performans ve yer tasarrufu arasında dengeli bir seçenek sunar.'
                      : product.thicknessMm && product.thicknessMm <= 40
                        ? 'güçlü sönümleme ve yüksek performans sağlar.'
                        : 'maksimum izolasyon ve premium performans hedefler.'}
                </p>
                <ul className={styles.seoList}>
                  <li>Stüdyo, müzik odası ve kayıt odası ses yalıtımı</li>
                  <li>Jeneratör odası, makine dairesi ve ekipman odalarında titreşim sönümleme</li>
                  <li>Kapı/duvar detaylarında darbe sesini azaltmaya yönelik katmanlı uygulamalar</li>
                  <li>Zemin altı şap ve kaplama altında destekleyici yalıtım katmanı</li>
                  {product.category === 'Bariyerli Bondex' || product.category === 'Çift Bariyerli Bondex' ? (
                    <li>Yüksek izolasyon gerektiren endüstriyel ve ticari uygulamalar</li>
                  ) : null}
                </ul>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>3</span>
                  <span className={styles.seoEmoji} aria-hidden="true">⚙️</span>
                  <h3 className={styles.seoTitle}>{product.name} özellikleri</h3>
                </header>
                <p className={styles.seoText}>
                  <strong>{product.name}</strong> seçiminde <strong>yoğunluk</strong>, <strong>kalınlık</strong> ve{' '}
                  <strong>uygulama yüzeyi</strong> kritik rol oynar. Uygulamada en iyi sonuç; doğru kalınlık, uygun
                  yapıştırma ve gerekiyorsa bariyer katmanı ile alınır.
                </p>
                <ul className={styles.seoList}>
                  {product.category === 'Bondex Sünger' ? (
                    <>
                      <li>Yüksek yoğunluk sayesinde darbe ve titreşim etkisini azaltmaya yardımcı olur</li>
                      <li>Kolay kesim ve uygulama: proje bazlı ölçü ve montaj</li>
                      <li>
                        Katmanlı sistemlerde{' '}
                        <Link to="/" rel="follow">
                          Bariyerli Bondex Sünger
                        </Link>{' '}
                        ile birlikte kullanılabilir
                      </li>
                    </>
                  ) : product.category === 'Bariyerli Bondex' ? (
                    <>
                      <li>Bariyer katmanı ile güçlendirilmiş kompozit yapı</li>
                      <li>Daha yüksek izolasyon performansı</li>
                      <li>
                        Klasik uygulamalar için{' '}
                        <Link to="/bondex-sunger" rel="follow">
                          Bondex sünger
                        </Link>{' '}
                        alternatifleri mevcuttur
                      </li>
                    </>
                  ) : (
                    <>
                      <li>İki bariyer katmanıyla premium maksimum izolasyon</li>
                      <li>En yüksek performans gerektiren projeler için ideal</li>
                      <li>
                        Daha düşük izolasyon hedefleri için{' '}
                        <Link to="/" rel="follow">
                          Bariyerli Bondex Sünger
                        </Link>{' '}
                        seçenekleri mevcuttur
                      </li>
                    </>
                  )}
                  <li>
                    Farklı kalınlık alternatifleri için{' '}
                    {product.category === 'Bondex Sünger' ? (
                      <Link to="/bondex-sunger" rel="follow">Bondex sünger</Link>
                    ) : product.category === 'Bariyerli Bondex' ? (
                      <Link to="/" rel="follow">Bariyerli Bondex Sünger</Link>
                    ) : (
                      <Link to="/cift-bariyerli-bondex-sunger" rel="follow">Çift Bariyerli Bondex Sünger</Link>
                    )}{' '}
                    sayfasından seçim yapılabilir
                  </li>
                </ul>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>4</span>
                  <span className={styles.seoEmoji} aria-hidden="true">💰</span>
                  <h3 className={styles.seoTitle}>{product.name} fiyatları</h3>
                </header>
                <p className={styles.seoText}>
                  <strong>{product.name}</strong> fiyatları; kalınlık, yoğunluk, plaka ölçüsü ve proje adedine göre değişir.
                  Fiyatlandırma metrekare (m²) başına yapılmaktadır. En doğru teklif için uygulama alanınızı (metraj),
                  hedef izolasyon seviyenizi ve tercih ettiğiniz kalınlığı paylaşmanız yeterlidir. Hızlı fiyat için WhatsApp üzerinden bilgi alabilirsiniz.
                </p>
              </section>

              <section className={styles.seoCard}>
                <header className={styles.seoHead}>
                  <span className={styles.seoNum}>5</span>
                  <span className={styles.seoEmoji} aria-hidden="true">💡</span>
                  <h3 className={styles.seoTitle}>{product.name} seçerken nelere dikkat edilmeli?</h3>
                </header>
                <p className={styles.seoText}>
                  <strong>{product.name}</strong> seçimi; uygulama alanı, hedef izolasyon seviyesi ve mevcut yükseklik kısıtlarına göre
                  yapılmalıdır. {product.thicknessMm
                    ? `${product.thicknessMm}mm kalınlık `
                    : 'Doğru kalınlık seçimi '}
                  {product.thicknessMm && product.thicknessMm <= 20
                    ? 'ince detaylar ve sınırlı alanlarda ideal bir çözümdür.'
                    : product.thicknessMm && product.thicknessMm <= 30
                      ? 'performans ve yer tasarrufu arasında dengeli bir seçenektir.'
                      : product.thicknessMm && product.thicknessMm <= 40
                        ? 'güçlü sönümleme gerektiren uygulamalar için idealdir.'
                        : 'maksimum izolasyon hedeflerinde tercih edilir.'}
                  {product.category === 'Bondex Sünger' ? (
                    <>
                      {' '}Daha yüksek izolasyon hedeflerinde{' '}
                      <Link to="/" rel="follow">
                        Bariyerli Bondex Sünger
                      </Link>{' '}
                      seçeneklerini inceleyin; diğer{' '}
                      <Link to="/bondex-sunger" rel="follow">
                        Bondex sünger
                      </Link>{' '}
                      kalınlıklarına göz atın.
                    </>
                  ) : product.category === 'Bariyerli Bondex' ? (
                    <>
                      {' '}Klasik uygulamalar için{' '}
                      <Link to="/bondex-sunger" rel="follow">
                        Bondex sünger
                      </Link>{' '}
                      modellerini inceleyin; daha yüksek izolasyon için{' '}
                      <Link to="/cift-bariyerli-bondex-sunger" rel="follow">
                        Çift Bariyerli Bondex Sünger
                      </Link>{' '}
                      seçeneklerini değerlendirin.
                    </>
                  ) : (
                    <>
                      {' '}Daha düşük izolasyon hedeflerinde{' '}
                      <Link to="/" rel="follow">
                        Bariyerli Bondex Sünger
                      </Link>{' '}
                      seçeneklerini inceleyin; klasik uygulamalar için{' '}
                      <Link to="/bondex-sunger" rel="follow">
                        Bondex sünger
                      </Link>{' '}
                      kalınlıklarına göz atın.
                    </>
                  )}
                </p>
              </section>
            </div>
          )}
        </>
      ),
    },
    {
      key: 'teknik',
      label: 'Teknik Özellikler',
      content: (
        <>
          <h2 className={styles.tabH2}>Teknik özellikler</h2>
          {specGridContent}
        </>
      ),
    },
    {
      key: 'kullanim',
      label: 'Kullanım Alanları',
      content: (
        <>
          <h2 className={styles.tabH2}>Kullanım alanları</h2>
          <div className={styles.usageGrid}>
            {usageAreas.map((area) => (
              <figure key={area.label} className={styles.usageCard}>
                <img className={styles.usageImage} src={area.image} alt={area.label} loading="lazy" />
                <figcaption className={styles.usageLabel}>{area.label}</figcaption>
              </figure>
            ))}
          </div>
        </>
      ),
    },
    {
      key: 'uygulama-gorselleri',
      label: 'Uygulama Görselleri',
      content: (
        <>
          <h2 className={styles.tabH2}>Uygulama görselleri</h2>
          <div className={styles.appGalleryGrid}>
            {applicationGalleryImages.map((src, index) => (
              <button
                key={src}
                type="button"
                className={styles.appGalleryThumb}
                onClick={(event) => openLightbox(src, event.currentTarget)}
                aria-label={`Uygulama görseli ${index + 1}`}
              >
                <img src={src} alt={`Uygulama görseli ${index + 1}`} loading="lazy" />
              </button>
            ))}
          </div>
        </>
      ),
    },
    {
      key: 'uygulama-videolari',
      label: 'Uygulama Videoları',
      content: (
        <>
          <h2 className={styles.tabH2}>Uygulama videoları</h2>
          <a
            className={styles.instagramLink}
            href="https://www.instagram.com/tasarimakustikmimari/"
            target="_blank"
            rel="nofollow noopener noreferrer"
            aria-label="Tasarım Akustik Mimari Instagram hesabı"
          >
            <IconInstagram />
            <span>Instagram&apos;da uygulama videolarını izleyin</span>
          </a>
        </>
      ),
    },
    {
      key: 'test-raporlari',
      label: 'Test Raporları',
      content: (
        <>
          <h2 className={styles.tabH2}>Test raporları</h2>
          <p className={styles.tabNote}>
            Bu kısım şu anda güncelleniyor. Yakında eklenecektir. Test raporlarını{' '}
            <a
              href={buildWhatsAppUrl('Merhaba, test raporları hakkında bilgi almak istiyorum.')}
              target="_blank"
              rel="nofollow noopener noreferrer"
            >
              {SALES_PHONE_READABLE}
            </a>{' '}
            numaramız üzerinden WhatsApp&apos;tan talep edebilirsiniz.
          </p>
        </>
      ),
    },
  ];

  return (
    <main className={`container ProductPage ${styles.productMain}`}>
      <ProductSEO product={seoProduct} />

      <nav className={styles.breadcrumb} aria-label="Breadcrumb">
        <Link to="/">Anasayfa</Link>
        <span aria-hidden="true">/</span>
        <Link to="/urunler">Ürünler</Link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">{product.name}</span>
      </nav>

      <div className={styles.heroGrid}>
        <div className={styles.media}>
          <div className={styles.mediaHeroSlot}>
            <OptimizedImage
              className={styles.img}
              src={selectedImage}
              alt={activeProduct.name}
              loading="eager"
              fetchPriority="high"
              decoding="async"
              variant="hero"
            />
          </div>
          {activeGallery.length > 1 && (
            <div className={styles.mediaThumbs} role="group" aria-label="Ürün görselleri">
              {activeGallery.map((imgPath, index) => (
                <button
                  key={`${imgPath}-${index}`}
                  type="button"
                  className={`${styles.mediaThumb} ${selectedImage === imgPath ? styles.mediaThumbActive : ''}`}
                  onClick={() => setSelectedImage(imgPath)}
                  aria-label={`${activeProduct.name} görsel ${index + 1}`}
                >
                  <OptimizedImage src={imgPath} alt={`${activeProduct.name} ${index + 1}`} variant="thumb" width={62} height={62} />
                </button>
              ))}
            </div>
          )}
          {activeGallery.length > 1 && (
            <>
              <button
                type="button"
                className={`${styles.mediaNavBtn} ${styles.mediaNavPrev}`}
                onClick={handlePrevImage}
                aria-label="Önceki görsel"
              >
                ‹
              </button>
              <button
                type="button"
                className={`${styles.mediaNavBtn} ${styles.mediaNavNext}`}
                onClick={handleNextImage}
                aria-label="Sonraki görsel"
              >
                ›
              </button>
            </>
          )}
        </div>

        <section className={`ProductSEO card ${styles.panel}`}>
          <h1 className={styles.h1}>
            {product.name}
          </h1>
          <p className={styles.modelLine}>
            <span className={styles.modelLabel}>Model:</span> {displayModel}
          </p>
          {isBariyerliHubPage && (
            <>
              <div className={styles.variantBox}>
                <span className={styles.variantLabel}>Tek Bariyerli Kalınlık Seçimi</span>
                <div className={styles.variantChips} role="radiogroup" aria-label="Tek bariyerli kalınlık seçimi">
                  {tekBariyerliThicknessOptions.map((thicknessMm) => (
                    <button
                      key={`tek-${thicknessMm}`}
                      type="button"
                      className={`${styles.variantChip} ${bariyerliVariantType === 'tek' && selectedThickness === thicknessMm ? styles.variantChipActive : ''}`}
                      onClick={() => {
                        setBariyerliVariantType('tek');
                        setSelectedThickness(thicknessMm);
                      }}
                      aria-pressed={bariyerliVariantType === 'tek' && selectedThickness === thicknessMm}
                    >
                      {thicknessMm}mm
                    </button>
                  ))}
                </div>
              </div>
              <div className={styles.variantBox}>
                <span className={styles.variantLabel}>Çift Bariyerli Kalınlık Seçimi</span>
                <div className={styles.variantChips} role="radiogroup" aria-label="Çift bariyerli kalınlık seçimi">
                  {ciftBariyerliThicknessOptions.map((thicknessMm) => (
                    <button
                      key={`cift-${thicknessMm}`}
                      type="button"
                      className={`${styles.variantChip} ${bariyerliVariantType === 'cift' && selectedThickness === thicknessMm ? styles.variantChipActive : ''}`}
                      onClick={() => {
                        setBariyerliVariantType('cift');
                        setSelectedThickness(thicknessMm);
                      }}
                      aria-pressed={bariyerliVariantType === 'cift' && selectedThickness === thicknessMm}
                    >
                      {thicknessMm}mm
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
          {isThicknessSelectable && (
            <div className={styles.variantBox}>
              <span className={styles.variantLabel}>Kalınlık Seçin</span>
              <div className={styles.variantChips} role="radiogroup" aria-label="Kalınlık seçimi">
                {thicknessOptions.map((thicknessMm) => (
                  <button
                    key={thicknessMm}
                    type="button"
                    className={`${styles.variantChip} ${selectedThickness === thicknessMm ? styles.variantChipActive : ''}`}
                    onClick={() => setSelectedThickness(thicknessMm)}
                    aria-pressed={selectedThickness === thicknessMm}
                  >
                    {thicknessMm}mm
                  </button>
                ))}
              </div>
            </div>
          )}
          {isAkustikSelectable && (
            <div className={styles.variantBox}>
              <span className={styles.variantLabel}>Kalınlık Seçin</span>
              <div className={styles.variantChips} role="radiogroup" aria-label="Akustik sünger kalınlık seçimi">
                {akustikThicknessOptions.map((thicknessMm) => (
                  <button
                    key={thicknessMm}
                    type="button"
                    className={`${styles.variantChip} ${selectedThickness === thicknessMm ? styles.variantChipActive : ''}`}
                    onClick={() => setSelectedThickness(thicknessMm)}
                    aria-pressed={selectedThickness === thicknessMm}
                  >
                    {thicknessMm}mm
                  </button>
                ))}
              </div>
            </div>
          )}
          {hasVariantPricing(product.category) && (
            <div className={styles.variantBox}>
              <span className={styles.variantLabel}>Yoğunluk Seçin</span>
              <div className={styles.variantChips} role="radiogroup" aria-label="Yoğunluk seçimi">
                {DENSITY_OPTIONS.map((dns) => (
                  <button
                    key={dns}
                    type="button"
                    className={`${styles.variantChip} ${selectedDensity === dns ? styles.variantChipActive : ''}`}
                    onClick={() => setSelectedDensity(dns)}
                    aria-pressed={selectedDensity === dns}
                  >
                    {dns} dns
                  </button>
                ))}
              </div>
            </div>
          )}
          {(isThicknessSelectable || isBariyerliHubPage || hasVariantPricing(product.category)) && (
            <p className={styles.selectedVariantSummary} aria-live="polite">
              Seçilen:{' '}
              <strong>
                {activeThicknessMm ? `${activeThicknessMm}mm` : '—'}
                {hasVariantPricing(product.category) ? ` · ${selectedDensity} dns` : ''}
                {activeVariantLine === 'bariyerli'
                  ? ' · Tek Bariyerli'
                  : activeVariantLine === 'cift' && isBariyerliHubPage
                    ? ' · Çift Bariyerli'
                    : ''}
              </strong>
              {typeof variantPrice === 'number' ? ` — ${formatPrice(variantPrice)}/m²` : ''}
            </p>
          )}
          <p className={styles.priceLine}>
            {displayPrice ? (
              <>
                <span className={styles.priceValue}>{displayPrice}</span>
                {activeProduct.priceUnit === 'm2' && (
                  <span className={styles.priceUnit}>/m²</span>
                )}
                <span className={styles.priceNote}>
                  {activeProduct.priceUnit === 'm2' ? '(Metrekare başına)' : ''}
                </span>
              </>
            ) : (
              <span className={styles.priceValue}>Fiyat için teklif alın</span>
            )}
          </p>
          {activeProduct.priceUnit === 'm2' && activeProduct.priceTRY && (
            <p className={styles.priceInfo}>
              <strong>Fiyatlandırma:</strong> Bu ürünün fiyatı metrekare (m²) başına belirlenmiştir.
              İhtiyacınız olan metrekare miktarını WhatsApp üzerinden paylaşarak toplam fiyat teklifi alabilirsiniz.
            </p>
          )}
          <p className={styles.meta}>
            {product.shortDescription} {/* İçerik içinde dofollow iç link (talep edildi) */}{' '}
            {product.category === 'Bondex Sünger' ? (
              <>
                <strong>Bondex sünger</strong> Türkiye'nin en kaliteli ses yalıtım süngeri çözümüdür. Daha güçlü
                izolasyon hedefliyorsanız{' '}
                <Link to="/" rel="follow">
                  <strong>Bariyerli Bondex Sünger</strong>
                </Link>{' '}
                seçeneklerini inceleyin; diğer{' '}
                <Link to="/bondex-sunger" rel="follow">
                  <strong>Bondex sünger</strong>
                </Link>{' '}
                kalınlıklarına göz atın.
              </>
            ) : product.category === 'Bariyerli Bondex' ? (
              <>
                <strong>Bariyerli Bondex Sünger</strong> Türkiye'nin en kaliteli bariyerli bondex sünger çözümüdür. Klasik{' '}
                <Link to="/bondex-sunger" rel="follow">
                  <strong>Bondex sünger</strong>
                </Link>{' '}
                modelleri için{' '}
                <Link to="/bondex-sunger" rel="follow">
                  Bondex sünger
                </Link>{' '}
                sayfasını inceleyin; diğer{' '}
                <Link to="/" rel="follow">
                  <strong>Bariyerli Bondex Sünger</strong>
                </Link>{' '}
                kalınlıklarına göz atın.
              </>
            ) : product.category === 'Akustik Sünger' ? (
              <>
                <strong>Akustik sünger</strong> yankı ve çınlamayı azaltan ses emici bir malzemedir. Komşu
                odadan ses geçişini engellemek için{' '}
                <Link to="/bondex-sunger" rel="follow">
                  <strong>Bondex sünger</strong>
                </Link>{' '}
                veya{' '}
                <Link to="/bariyerli-bondex-sunger" rel="follow">
                  <strong>Bariyerli Bondex Sünger</strong>
                </Link>{' '}
                modellerini inceleyin. Farklı yüzey profilleri için ürün sayfalarımıza göz atın.
              </>
            ) : (
              <>
                <strong>Çift Bariyerli Bondex Sünger</strong> premium maksimum izolasyon çözümüdür. Daha güçlü
                izolasyon hedefliyorsanız{' '}
                <Link to="/" rel="follow">
                  <strong>Bariyerli Bondex Sünger</strong>
                </Link>{' '}
                seçeneklerini inceleyin; klasik modeller için{' '}
                <Link to="/bondex-sunger" rel="follow">
                  <strong>Bondex sünger</strong>
                </Link>{' '}
                kalınlıklarına göz atın.
              </>
            )}
          </p>

          <div className={styles.badges}>
            <span className="chip">{product.category}</span>
            {activeProduct.thicknessMm ? <span className="chip">{activeProduct.thicknessMm}mm</span> : null}
            <span className="chip">Hızlı teklif</span>
            <span className="chip">WhatsApp sipariş</span>
          </div>

          <div className={styles.actions}>
            <button
              className={`${styles.actionBtn} ${styles.actionBtnCart}`}
              onClick={() => {
                cart.add(activeProduct, 1);
                toast.push({
                  title: 'Sepete eklendi',
                  description: activeProduct.name,
                  actionHref: '/sepet',
                  actionLabel: 'Sepete git',
                });
              }}
            >
              <IconCart /> Sepete ekle
            </button>
            <Link className={`${styles.actionBtn} ${styles.actionBtnGo}`} to="/sepet">
              Sepete git <IconArrowRight />
            </Link>
            <a className={styles.actionBtn} href={waUrl} target="_blank" rel="noopener noreferrer">
              <IconWhatsApp /> Şimdi Sipariş Ver! <IconArrowRight />
            </a>
          </div>

          {showPurchaseHighlights && (
            <div className={styles.purchaseHighlights} role="group" aria-label="Ürün özeti">
              {highlightSpecs.map((s) => (
                <div key={s.k} className={styles.purchaseHighlightItem}>
                  <span className={styles.purchaseHighlightK}>{s.k}</span>
                  <span className={styles.purchaseHighlightV}>{s.v}</span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      <div className={styles.leftInfoUnderHero}>
        <p className={styles.leftDesc}>{product.shortDescription}</p>
        <div className={styles.tags}>
          {product.slug === 'sungerpan' ? (
            <>
              <span className={styles.tag}>#sungerpan</span>
              <span className={styles.tag}>#bondex-sünger</span>
              <span className={styles.tag}>#rebonded-sünger</span>
            </>
          ) : (
            <>
              <span className={styles.tag}>#{product.slug}</span>
              <span className={styles.tag}>
                #
                {product.category === 'Bariyerli Bondex'
                  ? `bariyerli-bondex-sünger${product.thicknessMm ? `-${product.thicknessMm}mm` : ''}`
                  : product.category.toLowerCase().replace(/\s+/g, '-')}
              </span>
              {product.thicknessMm ? <span className={styles.tag}>#{product.thicknessMm}mm</span> : null}
            </>
          )}
          <span className={styles.tag}>#ses-yalitimi</span>
        </div>
      </div>

      <div className={styles.contentGrid}>
        <section>
          <div className={styles.sectionCard}>
            <Tabs items={tabItems} defaultKey="aciklama" />
          </div>
        </section>
      </div>

      {/* Reviews (separate section) */}
      {pageReviews.length ? (
        <section className={styles.reviewsSection}>
          <div className={styles.reviewsHeader}>
            <div>
              <h2 className={styles.reviewsTitle}>Müşteri Yorumları</h2>
              <div className={styles.reviewsUnderline} aria-hidden="true" />
            </div>
          </div>

          <div className={styles.reviewSlider}>
            <button
              type="button"
              className={styles.reviewNavBtn}
              onClick={() => setReviewIndex((prev) => Math.max(0, prev - 1))}
              disabled={reviewIndex === 0}
              aria-label="Önceki yorumlar"
            >
              ‹
            </button>

            <div className={styles.reviewViewport}>
              <div
                className={styles.reviewTrack}
                style={
                  {
                    transform: `translateX(-${reviewIndex * (100 / reviewsPerView)}%)`,
                    '--cards-per-view': reviewsPerView,
                  } as React.CSSProperties
                }
              >
                {pageReviews.map((r) => {
                  return (
                    <article key={`${r.name}-${r.date}`} className={styles.reviewCardPro}>
                      <div className={styles.reviewTopPro}>
                        <div className={styles.reviewMeta}>
                          <div className={styles.reviewNameRow}>
                            <p className={styles.reviewNamePro}>{r.name}</p>
                            <span className={styles.reviewDate}>{r.date}</span>
                          </div>
                          <span className={styles.reviewSubtitle}>{r.title}</span>
                        </div>
                      </div>

                      <div className={styles.ratingRow}>
                        <div className={styles.stars} role="img" aria-label={`${r.rating} üzerinden 5 yıldız`}>
                          {Array.from({ length: 5 }).map((_, i) => (
                            <span key={i} aria-hidden="true">
                              <IconStar />
                            </span>
                          ))}
                        </div>
                        <span className={styles.reviewBadge}>Mükemmel</span>
                      </div>

                      <p className={styles.reviewTextPro}>{r.text}</p>
                    </article>
                  );
                })}
              </div>
            </div>

            <button
              type="button"
              className={styles.reviewNavBtn}
              onClick={() => setReviewIndex((prev) => Math.min(maxReviewIndex, prev + 1))}
              disabled={reviewIndex >= maxReviewIndex}
              aria-label="Sonraki yorumlar"
            >
              ›
            </button>
          </div>
        </section>
      ) : null}

      {/* FAQ (separate section) */}
      {product.faqs?.length ? (
        <section className={styles.faqSection}>
          <div className={styles.faqHeader}>
            <h2 className={styles.faqTitle}>Sıkça Sorulan Sorular</h2>
            <div className={styles.faqUnderline} aria-hidden="true" />
          </div>
          <div className={styles.faqCard}>
            <Accordion items={product.faqs} variant="seo" />
          </div>
        </section>
      ) : null}

      {/* Related products */}
      {related.length ? (
        <section className={styles.relatedSection}>
          <div className={styles.relatedHeader}>
            <h2 className={styles.relatedTitle}>Aynı Kategoride Diğer Ürünler</h2>
            <div className={styles.relatedUnderline} aria-hidden="true" />
          </div>
          <div className={styles.relatedGrid}>
            {related.map((p) => (
              <article key={p.slug} className={styles.relatedCard}>
                <Link className={styles.relatedMedia} to={`/${p.slug}`} aria-label={p.name}>
                  <OptimizedImage src={p.imagePath} alt={p.name} variant="card" loading="lazy" />
                </Link>
                <div className={styles.relatedBody}>
                  <h3 className={styles.relatedName}>
                    <Link to={`/${p.slug}`}>{p.name}</Link>
                  </h3>
                  <p className={styles.relatedPrice}>
                    {typeof p.priceTRY === 'number' ? formatPrice(p.priceTRY) : 'Teklif'}
                    {p.priceUnit === 'm2' ? <span className={styles.relatedUnit}> /m²</span> : null}
                  </p>
                  <Link className={styles.relatedBtn} to={`/${p.slug}`}>
                    Hemen İncele
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {lightboxImage ? (
        <div
          className={styles.lightboxBackdrop}
          role="dialog"
          aria-modal="true"
          aria-label="Uygulama görseli önizleme"
          onClick={closeLightbox}
        >
          <button
            type="button"
            className={styles.lightboxClose}
            onClick={(event) => {
              event.stopPropagation();
              closeLightbox();
            }}
            aria-label="Kapat"
          >
            <IconClose />
          </button>
          <img
            className={styles.lightboxImage}
            src={lightboxImage}
            alt="Uygulama görseli"
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      ) : null}
    </main>
  );
}

